import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';
import HoverTooltip from './HoverTooltip';
import { buildSingleTooltipHTML, buildMultiTooltipHTML, pickMapInventory } from './tooltipHelpers';
import { preloadPhotos, photoCache } from '../Photos/photoCache';
import SatelliteOutlinedIcon from '@mui/icons-material/SatelliteOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import CropFreeIcon from '@mui/icons-material/CropFree';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { createRoot } from 'react-dom/client';
import { toast } from '../../../common/toast';
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import StableMuiDialog from "../../../common/StableMuiDialog.js"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Table from "@mui/material/Table"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TableCell from "@mui/material/TableCell"
import TableBody from "@mui/material/TableBody"
import PropertyRecordCard from './PropertyRecordCard';
import { usePersistedDataStore } from '../../store';
import { 
  getBasePaintProperties, 
  getClusterPaintProperties, 
  COLORS,
  STROKE_WIDTHS,
  getSubjectSaleIndicatorLayer,
  getSelectedCompsTextLayer,
  getClusterCountLayer,
  getMovedPointsLayer,
  getMovedPointsTextLayer,
  getUnselectedCompsNALTextLayer,
  resetAllStyling
} from './mapStyleUtils';
import useUpdateDB from '../../../common/updateDB.js';
import { getIFMVUtil } from '../../../utils/getAVUtil';
import logger from '../../../utils/logger';

// Initialize Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoianJidXJucyIsImEiOiJjbWE4Ynd6cmowOG5rMmtxOGNwcndveXRsIn0.waI6Q4F4bc96OVSeNtHmxw';

// Portal wrapper for overlays
const OverlayPortal = ({ children }) => {
  const el = document.getElementById('overlay-root');
  return el ? createPortal(children, el) : null;
};

// Shared filtering function for both radius and polygon filtering
const filterPointsByGeometry = (points, filterGeometry, onMapFilterChange) => {
  if (!filterGeometry) return points;
  
  const filteredPoints = points.filter(coord => {
    if (coord.isSubject) return true;
    
    const point = turf.point([coord.longitude, coord.latitude]);
    
    if (filterGeometry.type === 'radius') {
      const distance = turf.distance(
        point,
        turf.point(filterGeometry.center),
        { units: 'miles' }
      );
      return distance <= filterGeometry.radius;
    } else if (filterGeometry.type === 'polygon') {
      return filterGeometry.features.some(polygon => 
        turf.booleanPointInPolygon(point, polygon)
      );
    }
    
    return true;
  });

  // Call onMapFilterChange with filtered parcel IDs if it exists
  if (onMapFilterChange) {
    const filteredParcelIds = filteredPoints.map(coord => coord.parcel_id);
    onMapFilterChange(filteredParcelIds);
  }
  
  return filteredPoints;
};

// This desperately needs to have the draw functionality abstracted out, but more complicated than I'd like to deal wiht rn
// For performance should look into the updateCompData function, surely have some excess invocations in this. Same with useeffect chaining, likely some redundancy there.
const PropertyMap = ({
  latLongs = [], 
  onPolygonDraw = () => {},
  isCourtView = false,
  handleCompViewClose,
  handleProcessDBUpdate,
  externalSelectedProperty = null,
  updateSelectedComps,
  selectedComps,
  filteredParcelIds,
  onMapFilterChange,
  caseObject,
  negotiationData = null, // New prop for negotiation view data
  isNegotiationView = false, // New prop to indicate if we're in negotiation view
  subjectData = null, // Subject data for negotiation view
  isSaleRun = true, // New prop to indicate if we're in sale run
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const updateCompDataRef = useRef();
  const resetDrawStateRef = useRef();
  const startFreehandRef = useRef();
  const startRadiusRef = useRef();
  const polygonEntryGenRef = useRef(0);
  const activeDrawToolRef = useRef(null); // 'polygon' | 'freehand' | 'radius' | null
  const resettingDrawRef = useRef(false);
  const { updateDB } = useUpdateDB();
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Data preparation functions
  const parseCoordinates = useCallback((lat, lon) => ({
    latitude: typeof lat === 'string' ? parseFloat(lat) : lat,
    longitude: typeof lon === 'string' ? parseFloat(lon) : lon
  }), []);

  // Convert negotiation data to map format
  const convertNegotiationDataToMapFormat = useCallback((negotiationComps, subjectData) => {
    const result = [];

    // Add subject property first (always add if subjectData exists)
    if (subjectData) {
      const { latitude: subjectLat, longitude: subjectLon } = parseCoordinates(
        subjectData.Latitude?.[0],
        subjectData.Longitude?.[0]
      );

      if (subjectLat && subjectLon && !isNaN(subjectLat) && !isNaN(subjectLon)) {
        result.push({
          parcel_id: subjectData.parcel_id?.[0],
          id_number: 0,
          latitude: subjectLat,
          longitude: subjectLon,
          address: subjectData.Address?.[0] || '',
          town: subjectData.Town?.[0] || null,
          zip: subjectData.ZipCode?.[0] || null,
          Remarks: subjectData.Remarks?.[0] || null,
          SDCode: subjectData.SDCode?.[0] || null,
          SDName: subjectData.SDName?.[0] || null,
          MLSNumber: subjectData.MLSNumber?.[0] || null,
          MLSPhotoFolder: subjectData.MLSPhotoFolder?.[0] || null,
          SalesWebScreenshot: subjectData.SalesWebScreenshot?.[0] || null,
          isSubject: true,
          selected: true,
          selectedRowNumber: 0,
          ...pickMapInventory(subjectData, 0),
          ifmv: getIFMVUtil(subjectData, 0) || 0,
          salePrice: subjectData.SalePrice?.[0] || null,
          saleDate: subjectData.SaleDate?.[0] || null,
          TransactionID: subjectData.TransactionID?.[0] ?? null,
          adjustedPrice: subjectData.AdjustedPrice?.[0] || null,
          supportsReduction: false,
          ALFlag: subjectData.ALFlag?.[0] ?? ''
        });
      }
    }

    if (negotiationComps && negotiationComps.length > 0) {
      negotiationComps.forEach((comp, index) => {
        if (comp.Address && comp.Address !== '') {
          const { latitude, longitude } = parseCoordinates(
            comp.Latitude,
            comp.Longitude
          );

          if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
            const adjustedPrice = comp.AdjustedPrice || comp.SalePrice || null;
            const subjectIFMV = getIFMVUtil(subjectData, 0) || 0;
            const supportsReduction = adjustedPrice && subjectIFMV && adjustedPrice < subjectIFMV;

            result.push({
              parcel_id: comp.parcel_id,
              id_number: index + 1,
              latitude,
              longitude,
              address: comp.Address,
              town: comp.Town || null,
              zip: comp.ZipCode || null,
              MLSNumber: comp.MLSNumber || null,
              MLSPhotoFolder: comp.MLSPhotoFolder || null,
              SalesWebScreenshot: comp.SalesWebScreenshot || null,
              Remarks: comp.Remarks || null,
              SDCode: comp.SDCode || null,
              SDName: comp.SDName || null,
              isSubject: false,
              selected: true,
              selectedRowNumber: index + 1,
              ...pickMapInventory(comp),
              salePrice: comp.SalePrice || null,
              saleDate: comp.SaleDate || null,
              TransactionID: comp.TransactionID ?? null,
              adjustedPrice: adjustedPrice,
              supportsReduction: supportsReduction,
              ALFlag: comp.ALFlag ?? ''
            });
          }
        }
      });
    }

    return result;
  }, [parseCoordinates]);

  // // Convert regular comp data to map format -- this is not being used, im pretty sure you abstracted this rfurhter out.
  // const convertRegularDataToMapFormat = useCallback((sourceObject, selectedCompIds, savedComps) => {
  //   if (!sourceObject) return [];

  //   const selectedCompMap = {};
  //   selectedCompIds.forEach((pid, index) => {
  //     selectedCompMap[pid] = index + 1;
  //   });

  //   const { latitude: subjectLat, longitude: subjectLon } = parseCoordinates(
  //     sourceObject.Latitude[0],
  //     sourceObject.Longitude[0]
  //   );

  //   const result = [{
  //     parcel_id: sourceObject.parcel_id[0],
  //     id_number: 0,
  //     latitude: subjectLat,
  //     longitude: subjectLon,
  //     address: sourceObject.Address[0],
  //     town: sourceObject.Town?.[0] || null,
  //     zip: sourceObject.ZipCode?.[0] || null,
  //     Remarks: sourceObject.Remarks?.[0] || null,
  //     SDCode: sourceObject.SDCode?.[0] || null,
  //     SDName: sourceObject.SDName?.[0] || null,
  //     MLSNumber: sourceObject.MLSNumber?.[0] || null,
  //     isSubject: true,
  //     selected: true,
  //     selectedRowNumber: 0,
  //     style: sourceObject.Style?.[0] || '',
  //     sqft: sourceObject.Sqft?.[0] || 0,
  //     acres: sourceObject.Acres?.[0] || 0,
  //     ifmv: sourceObject.IFMV?.[0] || 0,
  //     salePrice: sourceObject.SalePrice?.[0] || null,
  //     saleDate: sourceObject.SaleDate?.[0] || null,
  //     adjustedPrice: sourceObject.AdjustedPrice?.[0] || null,
  //     supportsReduction: false
  //   }];

  //   if (sourceObject && sourceObject.parcel_id) {
  //     for (let i = 1; i < sourceObject.parcel_id.length + 1; i++) {
  //       const parcelId = sourceObject.parcel_id[i];
  //       const { latitude, longitude } = parseCoordinates(
  //         sourceObject.Latitude[i],
  //         sourceObject.Longitude[i]
  //       );
        
  //       // Include entries even without valid coordinates for address click functionality
  //       const isSelected = selectedCompIds.includes(parcelId);
  //       const selectedRowNumber = isSelected ? selectedCompMap[parcelId] : 0;
  //       const adjustedPrice = sourceObject.AdjustedPrice?.[i] || null;
  //       const subjectIFMV = sourceObject.IFMV?.[0] || 0;
  //       const supportsReduction = adjustedPrice && subjectIFMV && adjustedPrice < subjectIFMV;
        
  //       result.push({
  //         parcel_id: parcelId,
  //         id_number: i,
  //         latitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? latitude : null,
  //         longitude: (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) ? longitude : null,
  //         address: sourceObject.Address[i],
  //         town: sourceObject.Town?.[i] || null,
  //         zip: sourceObject.ZipCode?.[i] || null,
  //         MLSNumber: sourceObject.MLSNumber?.[i] || null,
  //         Remarks: sourceObject.Remarks?.[i] || null,
  //         SDCode: sourceObject.SDCode?.[i] || null,
  //         SDName: sourceObject.SDName?.[i] || null,
  //         isSubject: false,
  //         selected: isSelected,
  //         selectedRowNumber,
  //         style: sourceObject.Style?.[i] || '',
  //         sqft: sourceObject.Sqft?.[i] || 0,
  //         acres: sourceObject.Acres?.[i] || 0,
  //         salePrice: sourceObject.SalePrice?.[i] || null,
  //         saleDate: sourceObject.SaleDate?.[i] || null,
  //         adjustedPrice: adjustedPrice,
  //         supportsReduction: supportsReduction
  //       });
  //     }
  //   }

  //   return result;
  // }, [parseCoordinates]);

  // Memoized processed data for map
  const processedMapData = useMemo(() => {
    if (isNegotiationView) {
      // Process raw negotiation data (even if empty, we still want to show subject)
      return convertNegotiationDataToMapFormat(negotiationData || [], subjectData);
    } else if (latLongs && latLongs.length > 0) {
      return latLongs; // Already processed regular data
    }
    return [];
  }, [isNegotiationView, negotiationData, subjectData, convertNegotiationDataToMapFormat, latLongs]);

  // Function to fit bounds for all visible points
  const fitBoundsToAllPoints = useCallback(() => {
    if (!map.current || !processedMapData.length) return;

    // Get all points that should be displayed
    const allPoints = processedMapData.filter(point => {
      if (!point || typeof point !== 'object') return false;
      
      const lat = point.latitude ? parseFloat(point.latitude) : null;
      const lng = point.longitude ? parseFloat(point.longitude) : null;
      
      return lat && lng && !isNaN(lat) && !isNaN(lng) && 
             lat >= -90 && lat <= 90 && 
             lng >= -180 && lng <= 180;
    });

    if (!allPoints.length) return;

    // Create bounds object
    const bounds = new mapboxgl.LngLatBounds();

    // Extend bounds with all valid points
    allPoints.forEach(point => {
      const lng = parseFloat(point.longitude);
      const lat = parseFloat(point.latitude);
      bounds.extend([lng, lat]);
    });

    // Only fit bounds if we have valid coordinates
    if (bounds.isEmpty()) {
      // If no valid coordinates, center on US
      map.current.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 4,
        duration: 3000
      });
    } else {
      // Add some padding to the bounds
      const padding = {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50
      };

      // Fit map to bounds with padding
      map.current.fitBounds(bounds, {
        padding: padding,
        duration: 3000,
        maxZoom: 15 // Prevent zooming in too close
      });
    }
  }, [processedMapData]);

  // Function to fit bounds for selected comps only
  const fitBoundsToSelectedComps = useCallback(() => {
    if (!map.current || !processedMapData.length) return;

    // Get all selected comps and subject
    const selectedComps = processedMapData.filter(coord => coord.selected || coord.isSubject);
    if (!selectedComps.length) return;

    // Create bounds object
    const bounds = new mapboxgl.LngLatBounds();

    // Extend bounds with all selected comps that have valid coordinates
    selectedComps.forEach(coord => {
      const lng = parseFloat(coord.longitude);
      const lat = parseFloat(coord.latitude);
      
      if (!isNaN(lng) && !isNaN(lat) && 
          lng >= -180 && lng <= 180 && 
          lat >= -90 && lat <= 90) {
        bounds.extend([lng, lat]);
      } 
    });

    // Only fit bounds if we have valid coordinates
    if (bounds.isEmpty()) {
      // If no valid coordinates, center on US
      map.current.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 4,
        duration: 3000
      });
    } else {
      // Add some padding to the bounds
      const padding = {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50
      };

      // Fit map to bounds with padding
      map.current.fitBounds(bounds, {
        padding: padding,
        duration: 3000,
        maxZoom: 15 // Prevent zooming in too close
      });
    }
  }, [processedMapData]);
  const [isSatellite, setIsSatellite] = useState(false);
  // IF you want to limit # of comps you can do it with this state. (removed this functionality)
  const [compsToShow, setCompsToShow] = useState('100');
  const currentSubjectPid = useRef(null);
  const previousSubjectCoords = useRef(null);
  const [isFreehand, setIsFreehand] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [currentPropertyIndex, setCurrentPropertyIndex] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isFixingLatLong, setIsFixingLatLong] = useState(false);
  const [movedPoints, setMovedPoints] = useState({}); // Track moved points by parcel_id
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const propertyBeingEditedRef = useRef(null);
  const reportType = usePersistedDataStore((state) => state.reportType);
  const [isRadiusMode, setIsRadiusMode] = useState(false);
  const radiusCircleRef = useRef(null);
  const [radiusCircle, setRadiusCircle] = useState(null);
  const [legendCollapsed, setLegendCollapsed] = useState(false);
  const [mapContainerRect, setMapContainerRect] = useState(null);

  // getMode throws if Draw exists but isn't addControl'd yet
  const safeGetDrawMode = () => {
    try {
      return draw.current?.getMode?.() ?? null;
    } catch {
      return null;
    }
  };

  // Shared teardown for polygon / freehand / radius drawing modes.
  // skipModeChange: UI-only cleanup when Draw is already leaving a draw mode (e.g. draw.create).
  const resetDrawState = useCallback(({
    clearFeatures = false,
    clearRadius = false,
    clearFilter = false,
    refreshMap = false,
    skipModeChange = false,
  } = {}) => {
    if (!map.current || resettingDrawRef.current) return;
    resettingDrawRef.current = true;
    polygonEntryGenRef.current += 1;
    activeDrawToolRef.current = null;

    try {
      if (map.current.freehandHandlers) {
        const { onMouseDown, onMouseMove, onMouseUp, onMouseOver } = map.current.freehandHandlers;
        map.current.off('mousedown', onMouseDown);
        map.current.off('mousemove', onMouseMove);
        map.current.off('mouseup', onMouseUp);
        map.current.off('mouseover', onMouseOver);
        map.current.freehandHandlers = null;
      }

      if (map.current.radiusHandlers) {
        const {
          handleMouseMove,
          handleClick,
          handleRadiusDrag,
          handleMouseEnter,
          handleMouseLeave,
          handleMouseDown,
        } = map.current.radiusHandlers;
        map.current.off('mousemove', handleMouseMove);
        map.current.off('click', handleClick);
        map.current.off('mousemove', handleRadiusDrag);
        map.current.off('mouseenter', 'radius-border-layer', handleMouseEnter);
        map.current.off('mouseleave', 'radius-border-layer', handleMouseLeave);
        map.current.off('mousedown', 'radius-border-layer', handleMouseDown);
        map.current.radiusHandlers = null;
      }

      if (draw.current && !skipModeChange) {
        // trash() mid-draw; changeMode('simple_select') while in draw_polygon overflows via nested onStop
        const mode = safeGetDrawMode();
        if (mode === 'draw_polygon' || mode === 'draw_line_string' || mode === 'draw_point') {
          try { draw.current.trash?.(); } catch { /* draw may be mid-teardown */ }
        } else if (mode && mode !== 'simple_select') {
          try { draw.current.changeMode('simple_select'); } catch { /* ignore */ }
        }
        if (clearFeatures) {
          try { draw.current.deleteAll?.(); } catch { /* ignore */ }
        }
      } else if (draw.current && clearFeatures) {
        try { draw.current.deleteAll?.(); } catch { /* ignore */ }
      }

      if (map.current.getSource('drawing-preview')) {
        map.current.getSource('drawing-preview').setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [] },
        });
      }

      if (clearRadius) {
        ['radius-border-layer', 'radius-circle-layer', 'radius-line-layer', 'radius-label-layer'].forEach((id) => {
          if (map.current.getLayer(id)) map.current.removeLayer(id);
        });
        ['radius-circle', 'radius-line', 'radius-label'].forEach((id) => {
          if (map.current.getSource(id)) map.current.removeSource(id);
        });
        radiusCircleRef.current = null;
        setRadiusCircle(null);
      }

      ['.mapboxgl-ctrl-polygon', '.mapboxgl-ctrl-freehand', '.mapboxgl-ctrl-radius'].forEach((sel) => {
        document.querySelector(sel)?.classList.remove('active');
      });
      mapContainer.current?.classList.remove('polygon-drawing', 'freehand-drawing', 'radius-drawing');
      if (map.current.getCanvas()) map.current.getCanvas().style.cursor = '';
      map.current.dragPan?.enable();

      setIsFreehand(false);
      setIsDrawing(false);
      setIsRadiusMode(false);

      if (clearFilter && onMapFilterChange) onMapFilterChange([]);
      if (refreshMap && updateCompDataRef.current) updateCompDataRef.current();
    } catch (error) {
      logger.warn('Error during draw reset:', error);
    } finally {
      resettingDrawRef.current = false;
    }
  }, [onMapFilterChange]);

  const enterPolygonDrawMode = useCallback(() => {
    resetDrawState();
    const entryGen = polygonEntryGenRef.current;
    activeDrawToolRef.current = 'polygon';
    document.querySelector('.mapboxgl-ctrl-polygon')?.classList.add('active');
    mapContainer.current?.classList.add('polygon-drawing');
    setIsDrawing(true);

    requestAnimationFrame(() => {
      if (entryGen !== polygonEntryGenRef.current || activeDrawToolRef.current !== 'polygon') return;
      if (!draw.current || !map.current || resettingDrawRef.current) return;
      try {
        const mode = safeGetDrawMode();
        if (mode === 'draw_polygon' || mode === 'draw_line_string' || mode === 'draw_point') {
          draw.current.trash?.();
        }
        draw.current.changeMode('draw_polygon');
      } catch (error) {
        logger.warn('Failed to enter draw_polygon:', error);
        return;
      }
      if (entryGen !== polygonEntryGenRef.current || activeDrawToolRef.current !== 'polygon') return;
      map.current.dragPan.disable();
      if (map.current.getCanvas()) map.current.getCanvas().style.cursor = 'crosshair';
    });
  }, [resetDrawState]);

  useEffect(() => {
    resetDrawStateRef.current = resetDrawState;
  }, [resetDrawState]);

  const enterPolygonDrawModeRef = useRef();
  useEffect(() => {
    enterPolygonDrawModeRef.current = enterPolygonDrawMode;
  }, [enterPolygonDrawMode]);

  // Add key down handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && selectedProperty) {
      setSelectedProperty(null);
    }
  }, [selectedProperty]);

  // Calculate map container position for portal positioning
  useEffect(() => {
    const updateMapContainerRect = () => {
      if (mapContainer.current) {
        const rect = mapContainer.current.getBoundingClientRect();
        setMapContainerRect(rect);
      }
    };

    updateMapContainerRect();
    window.addEventListener('resize', updateMapContainerRect);
    window.addEventListener('scroll', updateMapContainerRect);

    return () => {
      window.removeEventListener('resize', updateMapContainerRect);
      window.removeEventListener('scroll', updateMapContainerRect);
    };
  }, []);

  // Initialize MapboxDraw
  useEffect(() => {
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      attributionControl: false,
      controls: {
        polygon: false, // Disable default polygon control
        trash: false    // Disable default trash control
      },
      styles: [
        {
          'id': 'gl-draw-polygon-fill',
          'type': 'fill',
          'filter': ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          'paint': {
            'fill-color': '#1976d2',
            'fill-outline-color': '#1976d2',
            'fill-opacity': 0.1
          }
        },
        {
          'id': 'gl-draw-polygon-stroke',
          'type': 'line',
          'filter': ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          'paint': {
            'line-color': '#1976d2',
            'line-width': 3
          }
        },
        {
          'id': 'gl-draw-polygon-and-line-vertex-active',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 5,
            'circle-color': '#FFFFFF',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#1976d2'
          }
        },
        {
          'id': 'gl-draw-polygon-and-line-midpoint',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 4,
            'circle-color': '#FFFFFF',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#1976d2'
          }
        },
        {
          'id': 'gl-draw-polygon-and-line-vertex-hover',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 5,
            'circle-color': '#FFFFFF',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#1976d2'
          }
        },
        {
          'id': 'gl-draw-polygon-and-line-midpoint-hover',
          'type': 'circle',
          'filter': ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          'paint': {
            'circle-radius': 4,
            'circle-color': '#FFFFFF',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#1976d2'
          }
        }
      ]
    });
  }, []);

  // Memoize the photo preloading function
  const memoizedPreloadPhotos = useMemo(() => {
    // Get only new PIDs that haven't been attempted to load yet
    const newPids = latLongs
      .map(point => point.parcel_id)
      .filter(pid => pid && !photoCache.hasAttemptedLoad(pid));
    
    return () => {
      if (newPids.length > 0) {
        preloadPhotos(newPids.map(pid => ({ parcel_id: pid })));
      }
    };
  }, [processedMapData.map(point => point.parcel_id).join(',')]); // Only recompute if the actual parcel IDs change

  // Preload photos when coordinates change
  useEffect(() => {
    memoizedPreloadPhotos();
  }, [memoizedPreloadPhotos]);

  // Function which is called to update the points on the map + cluster them, etc.
  const updateCompData = useCallback(() => {
    if (!map.current || !map.current.getSource('comps')) return;

    try {
      // Get all points to display and ensure they exist
      // Use processed data (already converted to map format)
      let pointsToDisplay = Array.isArray(processedMapData) ? [...processedMapData] : [];

      // Track invalid coordinates to avoid duplicate toasts
      const invalidCoords = new Set();

      // Check for points with missing lat/long (only show errors for subject properties)
      pointsToDisplay.forEach(point => {
        if (!point || typeof point !== 'object') return;
        
        const lat = point.latitude ? parseFloat(point.latitude) : null;
        const lng = point.longitude ? parseFloat(point.longitude) : null;
        
        // Only show error for subject properties without coordinates
        if (point.isSubject && (!lat || !lng || isNaN(lat) || isNaN(lng))) {
          const coordKey = `${point.parcel_id}`;
          if (!invalidCoords.has(coordKey)) {
            invalidCoords.add(coordKey);
            const address = point.address || 'Unknown Address';
            const parcelId = point.parcel_id || 'Unknown Parcel ID';
            toast.error(`Invalid Lat/Long for SUBJECT: ${address} (Parcel ID: ${parcelId})`);
          }
        }
      });

      // Filter out points with missing or invalid coordinates
      pointsToDisplay = pointsToDisplay.filter(point => {
        if (!point || typeof point !== 'object') return false;
        
        const lat = point.latitude ? parseFloat(point.latitude) : null;
        const lng = point.longitude ? parseFloat(point.longitude) : null;
        
        return lat && lng && !isNaN(lat) && !isNaN(lng) && 
               lat >= -90 && lat <= 90 && // Valid latitude range
               lng >= -180 && lng <= 180; // Valid longitude range
      });

      // Find the subject property and get its IFMV
      const subjectProperty = pointsToDisplay.find(point => point.isSubject);
      const subjectIFMV = subjectProperty?.ifmv || 0;

      // Skip filtering in negotiation view - show all negotiation comps
      if (!isNegotiationView) {
        // Filter points based on compsToShow
        if (compsToShow === 'reduction') {
          pointsToDisplay = pointsToDisplay.filter(point => point.supportsReduction);
        } else if (compsToShow === 'no-reduction') {
          pointsToDisplay = pointsToDisplay.filter(point => !point.supportsReduction);
        }
        // First apply map filtering (radius or polygon)
        const drawnFeatures = draw.current?.getAll();
        if (drawnFeatures && drawnFeatures.features.length > 0) {
          pointsToDisplay = filterPointsByGeometry(pointsToDisplay, {
            type: 'polygon',
            features: drawnFeatures.features
          }, onMapFilterChange);
        } else if (radiusCircle) {
          pointsToDisplay = filterPointsByGeometry(pointsToDisplay, {
            type: 'radius',
            center: radiusCircle.center,
            radius: radiusCircle.radius
          }, onMapFilterChange);
        }
        // Then apply grid filtering if filteredParcelIds exists
        if (filteredParcelIds && filteredParcelIds.length > 0) {
          pointsToDisplay = pointsToDisplay.filter(point =>
            point.isSubject || point.selected || filteredParcelIds.includes(point.parcel_id)
          );
        }
      }

      // Process the input data
      let processedLatLongs = [...pointsToDisplay];
      
      // Check if first two items are the same parcel (subject sale)
      const isSubjectSale = processedLatLongs.length >= 2 && 
        processedLatLongs[0].parcel_id === processedLatLongs[1].parcel_id;
      
      // Filter out duplicate subject sale points
      if (isSubjectSale) {
        processedLatLongs = processedLatLongs.filter((coord, index) => {
          if (index === 0) return true; // Keep the first occurrence
          return coord.parcel_id !== processedLatLongs[0].parcel_id;
        });
      }
      
      // Apply compsToShow filter
      const display = processedLatLongs.filter(coord => {
        if (coord.isSubject) return true;
        if (coord.selected) return true;
        if (compsToShow === 'all') return true;
        const unselectedComps = processedLatLongs.filter(c => !c.selected && !c.isSubject);
        const index = unselectedComps.indexOf(coord);
        return index < parseInt(compsToShow);
      });

      // Create GeoJSON feature collection
      const geojsonData = {
        type: 'FeatureCollection',
        features: display.map((coord, index) => {
          // Check if this point has been moved
          const movedPoint = movedPoints[coord.parcel_id];
          const finalCoord = movedPoint ? {
            ...coord,
            longitude: movedPoint.newLongitude,
            latitude: movedPoint.newLatitude
          } : coord;

          const supportsReduction = finalCoord.adjustedPrice && subjectIFMV && finalCoord.adjustedPrice < subjectIFMV;
          const reductionPercentage = finalCoord.adjustedPrice && subjectIFMV ? 
            ((finalCoord.adjustedPrice - subjectIFMV) / subjectIFMV) * 100 : null;
          
          return {
            type: 'Feature',
            // Include selection in the id so clustered GeoJSON rebuilds on reorder.
            // Property-only updates on a stable parcel_id are ignored by the cluster worker.
            id: `${finalCoord.parcel_id}:${finalCoord.selected ? 1 : 0}:${finalCoord.selectedRowNumber || 0}`,
            properties: {
              ...finalCoord,
              isSubject: finalCoord.isSubject,
              selected: finalCoord.selected,
              selectedRowNumber: finalCoord.selectedRowNumber || 0,
              supportsReduction: supportsReduction || false,
              isModified: !!movedPoint,
              isSubjectSale: isSubjectSale && finalCoord.isSubject,
              ALFlag: finalCoord.ALFlag ?? ''
            },
            geometry: {
              type: 'Point',
              coordinates: [finalCoord.longitude, finalCoord.latitude]
            }
          };
        })
      };

      map.current.getSource('comps').setData(geojsonData);
    } catch (error) {
      logger.error('Error updating comp data:', error);
    }
  }, [processedMapData, movedPoints, radiusCircle, compsToShow, selectedComps, onMapFilterChange, filteredParcelIds]);

  // Add this effect right after the updateCompData definition
  useEffect(() => {
    updateCompDataRef.current = updateCompData;
  }, [updateCompData]);

  // Add effect to handle subject changes - this cleans up any drawn polygons
  useEffect(() => {
    // Find the current subject
    const subjectCoord = processedMapData.find(coord => coord.isSubject);
    
    // Only perform cleanup if subject has changed
    if (subjectCoord && currentSubjectPid.current !== subjectCoord.parcel_id) {
      // Update the current subject ID
      currentSubjectPid.current = subjectCoord.parcel_id;

      // Clear any existing popups
      if (map.current?.hoverPopup) {
        map.current.hoverPopup.remove();
        map.current.hoverPopup = null;
      }
      if (map.current?.clickPopup) {
        map.current.clickPopup.remove();
        map.current.clickPopup = null;
      }

      // Do NOT reset property record card state when subject changes
      // PropertyRecordCard should stay open regardless of subject/comp changes
      // However, if the currently selected property is the old subject, update it to the new subject
      if (selectedProperty && selectedProperty.isSubject && selectedProperty.parcel_id !== subjectCoord.parcel_id) {
        setSelectedProperty(subjectCoord);
        setSelectedProperties([subjectCoord]);
        setCurrentPropertyIndex(0);
        setSelectedPointId(subjectCoord.parcel_id);
      }

      // Clean up movedPoints if there are any
      if (Object.keys(movedPoints).length > 0) {
        setMovedPoints({});
      }

      resetDrawState({
        clearFeatures: true,
        clearRadius: true,
        clearFilter: true,
        refreshMap: true,
      });
    }
  }, [processedMapData, movedPoints, resetDrawState]);

  // update map when latLongs changes
  useEffect(() => {
    // logger.log('Updating map when latLongs changes');
    // logger.log('map.current', map.current);
    // logger.log('map.current.getSource("comps")', map.current?.getSource('comps'));
    if (map.current && map.current.getSource('comps')) {
      updateCompData();
    }
  }, [processedMapData, updateCompData, filteredParcelIds]);

  // Auto-fit bounds when negotiation comps are added
  useEffect(() => {
    if (isNegotiationView && processedMapData.length > 0) {
      // Small delay to ensure map is ready
      const timer = setTimeout(() => {
        fitBoundsToAllPoints();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isNegotiationView, processedMapData, fitBoundsToAllPoints]);

  // Update AL "X" display layers when reportType changes
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;

    const isAssessment = reportType === 'assessment';
    const selectedLayerId = 'selected-comps-text';
    const unselectedLayerId = 'unselected-comps-nal-text';

    // Update selected comps text layer
    if (map.current.getLayer(selectedLayerId)) {
      const updatedLayer = getSelectedCompsTextLayer(reportType);
      map.current.removeLayer(selectedLayerId);
      map.current.addLayer(updatedLayer);
    }

    // Add or remove unselected comps NAL layer based on reportType
    if (isAssessment && map.current.getLayer(unselectedLayerId)) {
      map.current.removeLayer(unselectedLayerId);
    } else if (!isAssessment && !map.current.getLayer(unselectedLayerId)) {
      map.current.addLayer(getUnselectedCompsNALTextLayer());
    }
  }, [reportType]);

  const startFreehand = useCallback(() => {
    // Don't gate on map.loaded() — after comp updates it can be false while the map is interactive
    if (!map.current?.getCanvas?.()) return;

    if (map.current.freehandHandlers) {
      const { onMouseDown, onMouseMove, onMouseUp, onMouseOver } = map.current.freehandHandlers;
      map.current.off('mousedown', onMouseDown);
      map.current.off('mousemove', onMouseMove);
      map.current.off('mouseup', onMouseUp);
      map.current.off('mouseover', onMouseOver);
      map.current.freehandHandlers = null;
    }

    activeDrawToolRef.current = 'freehand';
    map.current.getCanvas().style.cursor = 'crosshair';

    // Create a source for the drawing preview if it doesn't exist
    if (!map.current.getSource('drawing-preview')) {
      map.current.addSource('drawing-preview', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      });

      map.current.addLayer({
        id: 'drawing-preview',
        type: 'line',
        source: 'drawing-preview',
        paint: {
          'line-color': '#1976d2',
          'line-width': 2,
          'line-dasharray': [2, 2]
        }
      });
    }

    let coordinates = [];
    let isDrawing = false;

    const onMouseDown = (e) => {
      if (activeDrawToolRef.current !== 'freehand') return;
      isDrawing = true;
      coordinates = [[e.lngLat.lng, e.lngLat.lat]];
      map.current.getSource('drawing-preview').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      });
      if (map.current?.getCanvas()) {
        map.current.getCanvas().style.cursor = 'crosshair';
      }
    };

    const onMouseMove = (e) => {
      if (!isDrawing) return;
      coordinates.push([e.lngLat.lng, e.lngLat.lat]);
      map.current.getSource('drawing-preview').setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      });
      if (map.current?.getCanvas()) {
        map.current.getCanvas().style.cursor = 'crosshair';
      }
    };

    const onMouseUp = () => {
      if (!isDrawing) return;
      isDrawing = false;

      if (coordinates.length > 2) {
        coordinates.push(coordinates[0]);
        draw.current.add({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates]
          }
        });

        if (updateCompDataRef.current) {
          updateCompDataRef.current();
        } else {
          updateCompData();
        }
      }

      resetDrawStateRef.current?.();
    };

    map.current.on('mousedown', onMouseDown);
    map.current.on('mousemove', onMouseMove);
    map.current.on('mouseup', onMouseUp);

    const onMouseOver = () => {
      if (map.current?.freehandHandlers && map.current?.getCanvas()) {
        map.current.getCanvas().style.cursor = 'crosshair';
      }
    };
    map.current.on('mouseover', onMouseOver);

    map.current.freehandHandlers = { onMouseDown, onMouseMove, onMouseUp, onMouseOver };
  }, [updateCompData]);

  useEffect(() => {
    startFreehandRef.current = startFreehand;
  }, [startFreehand]);

  // Add effect to handle external property selection
  useEffect(() => {
    if (externalSelectedProperty) {
      const targetParcelId = externalSelectedProperty.parcel_id;
      const point = processedMapData.find(p => p.parcel_id === targetParcelId);

      if (point) {
        const mergedPoint = {
          ...point,
          ...(externalSelectedProperty.focusInternalCaseNotes && { focusInternalCaseNotes: externalSelectedProperty.focusInternalCaseNotes }),
          ...(externalSelectedProperty.focusOperatorMemos && { focusOperatorMemos: externalSelectedProperty.focusOperatorMemos }),
          ...(externalSelectedProperty.focusHomeownerNotes && { focusHomeownerNotes: externalSelectedProperty.focusHomeownerNotes }),
        };
        setSelectedProperty(mergedPoint);
        setSelectedProperties([mergedPoint]);
        setCurrentPropertyIndex(0);
        setSelectedPointId(mergedPoint.parcel_id);

        // Update point styling for selected point
        ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'].forEach(layerId => {
          if (map.current && map.current.getLayer(layerId)) {
            map.current.setPaintProperty(layerId, 'circle-color', [
              'case',
              ['==', ['get', 'parcel_id'], mergedPoint.parcel_id],
              '#FFFFFF',
              layerId === 'subject-layer' ? '#FFD700' :
              layerId === 'selected-comps-layer' ? '#4169E1' :
              layerId === 'reduction-comps-layer' ? '#15803d' : '#ef4444'
            ]);

            map.current.setPaintProperty(layerId, 'circle-stroke-color', [
              'case',
              ['==', ['get', 'parcel_id'], mergedPoint.parcel_id],
              '#4169E1',
              '#FFFFFF'
            ]);

            map.current.setPaintProperty(layerId, 'circle-stroke-width', [
              'case',
              ['==', ['get', 'parcel_id'], mergedPoint.parcel_id],
              3,
              layerId === 'subject-layer' ? 2 : 1
            ]);
          }
        });
      }
    }
  }, [externalSelectedProperty]);

  // Get map style URL based on current mode
  const getMapStyle = useCallback(() => {
    return isDarkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/streets-v12';
  }, [isDarkMode]);

  const getSatelliteStyle = useCallback(() => {
    return isDarkMode ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/mapbox/satellite-streets-v12';
  }, [isDarkMode]);
    
  // Initialize map exactly once on mount
  useEffect(() => {
    if (!mapContainer.current) {
      logger.log('No map container, returning');
      return;
    }

    try {
      // Find subject for initial centering
      const subjectCoord = latLongs.find(coord => coord.isSubject);
      let center, zoom;

      // Validate subject coordinates
      if (subjectCoord && 
          subjectCoord.longitude && 
          subjectCoord.latitude && 
          !isNaN(parseFloat(subjectCoord.longitude)) && 
          !isNaN(parseFloat(subjectCoord.latitude))) {
        const lng = parseFloat(subjectCoord.longitude);
        const lat = parseFloat(subjectCoord.latitude);
        
        // Additional validation for coordinate ranges
        if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
          center = [lng, lat];
          zoom = 12;
          
          // Store initial subject coordinates
          previousSubjectCoords.current = {
            longitude: lng,
            latitude: lat,
            parcel_id: subjectCoord.parcel_id
          };
        } else {
          center = [-98.5795, 39.8283];
          zoom = 4;
          toast.error(`Invalid coordinate ranges for subject property: ${subjectCoord.address || 'Unknown Address'} (Parcel ID: ${subjectCoord.parcel_id || 'Unknown'})`);
        }
      } else {
        // Default to US center if subject coordinates are invalid
        center = [-98.5795, 39.8283];
        zoom = 4;
        
        // Show error toast if subject coordinates are invalid
      }
      
      // Create new map with error handling
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: getMapStyle(),
        center: center,
        zoom: zoom,
        preserveDrawingBuffer: true,
        attributionControl: false,
        failIfMajorPerformanceCaveat: false,
        doubleClickZoom: false
      });

      // logger.log('Map instance created, setting up event handlers');

      // Add load handler with error handling
      map.current.on('load', () => {
        try {
          // logger.log('Map loaded, initializing...');
          // Initialize popup tracking variables and layer state flag
          map.current.hoverPopup = null;
          map.current.clickPopup = null;
          map.current.isLayerHovered = false;
          
          // logger.log('Adding navigation control');
          // Add navigation control
          map.current.addControl(new mapboxgl.NavigationControl({
            showCompass: false
          }), 'top-left');

          // logger.log('Adding comps source');
          // Initialize the source and layers
          map.current.addSource('comps', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            },
            cluster: true,
            clusterRadius: 8,
            clusterProperties: {
              has_subject: ['any', ['==', ['get', 'isSubject'], true]],
              has_selected: ['any', ['==', ['get', 'selected'], true]],
              all_green: ['all', ['==', ['get', 'supportsReduction'], true]],
              all_red: ['all', ['==', ['get', 'supportsReduction'], false]]
            }
          });

          // Add clusters layer
          map.current.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'comps',
            filter: ['has', 'point_count'],
            paint: getClusterPaintProperties()
          });

          // Add cluster count layer
          map.current.addLayer(getClusterCountLayer());

          // Add unclustered points layer
          map.current.addLayer({
            id: 'unclustered-point',
            type: 'circle',
            source: 'comps',
            filter: ['!', ['has', 'point_count']],
            paint: getBasePaintProperties()
          });

          // Add click handler for clusters
          map.current.on('click', 'clusters', (e) => {
            // Stop the event from propagating to other handlers
            e.originalEvent.stopPropagation();

            const features = map.current.queryRenderedFeatures(e.point, {
              layers: ['clusters']
            });
            
            if (!features || features.length === 0) return;
            
            const clusterId = features[0].properties.cluster_id;
            const pointCount = features[0].properties.point_count;

            // Get the cluster leaves
            map.current.getSource('comps').getClusterLeaves(
              clusterId,
              pointCount,
              0,
              (err, leaves) => {
                if (err) return;
                
                // Format the leaves to match our existing tooltip format
                const formattedLeaves = leaves.map(leaf => ({
                  ...leaf.properties,
                  longitude: leaf.geometry.coordinates[0],
                  latitude: leaf.geometry.coordinates[1],
                  id: leaf.properties.parcel_id
                }));

                logger.log('cluster trick triggering')
                // Set the first property as selected
                const firstProperty = formattedLeaves[0];
                setSelectedProperty(firstProperty);
                setSelectedProperties(formattedLeaves);
                setCurrentPropertyIndex(0);
                setSelectedPointId(firstProperty.parcel_id);

                // Update cluster styling for only the selected cluster
                if (map.current.getLayer('clusters')) {
                  map.current.setPaintProperty('clusters', 'circle-color', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    COLORS.WHITE,
                    ['==', ['get', 'has_subject'], true], COLORS.SUBJECT,
                    ['==', ['get', 'has_selected'], true], COLORS.SELECTED,
                    ['==', ['get', 'all_green'], true], COLORS.REDUCTION,
                    ['==', ['get', 'all_red'], true], COLORS.NO_REDUCTION,
                    COLORS.MIXED
                  ]);
                  map.current.setPaintProperty('clusters', 'circle-stroke-color', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    COLORS.SELECTED,
                    COLORS.BORDER
                  ]);
                  map.current.setPaintProperty('clusters', 'circle-stroke-width', [
                    'case',
                    ['==', ['get', 'cluster_id'], clusterId],
                    STROKE_WIDTHS.MODIFIED,
                    STROKE_WIDTHS.SUBJECT
                  ]);
                }
              }
            );
          });

          // Add click handler for map background to clear selection
          map.current.on('click', (e) => {
            // Check if we clicked on a cluster or point
            const features = map.current.queryRenderedFeatures(e.point, {
              layers: ['clusters', 'subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer']
            });

            // If we didn't click on any features, clear the selection
            if (features.length === 0) {
              // Reset all styling including clusters
              resetAllStyling(map.current, true);

              // Clear selection
              setSelectedProperty(null);
              setSelectedProperties([]);
              setCurrentPropertyIndex(0);
              setSelectedPointId(null);
            }
          });

          // Add hover effects for clusters
          map.current.on('mouseenter', 'clusters', () => {
            map.current.getCanvas().style.cursor = 'pointer';
          });

          map.current.on('mouseleave', 'clusters', () => {
            map.current.getCanvas().style.cursor = '';
          });

          // Add zoom end handler to clean up tooltips
          map.current.on('zoomend', () => {
            // Remove any existing tooltips when zooming
            if (map.current.hoverPopup) {
              map.current.hoverPopup.remove();
              map.current.hoverPopup = null;
            }
            if (map.current.clusterPopup) {
              map.current.clusterPopup.remove();
              map.current.clusterPopup = null;
            }
          });


          // logger.log('Adding satellite source');
          // Add satellite raster source
          map.current.addSource('satellite', {
            type: 'raster',
            tiles: [
              'https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=pk.eyJ1IjoianJidXJucyIsImEiOiJjbWE4Ynd6cmowOG5rMmtxOGNwcndveXRsIn0.waI6Q4F4bc96OVSeNtHmxw'
            ],
            tileSize: 512,
            maxzoom: 20
          });

          // logger.log('Adding satellite layer');
          const firstSymbol = map.current.getStyle().layers.find(l => l.type === 'symbol').id;
          map.current.addLayer({
            id: 'satellite-raster-layer',
            type: 'raster',
            source: 'satellite',
            paint: { 'raster-fade-duration': 0 },
            layout: { visibility: isSatellite ? 'visible' : 'none' }
          }, firstSymbol);

          // logger.log('Adding draw control');
          // Add draw control
          map.current.addControl(draw.current, 'top-left');

          // --- CUSTOM TOOLBAR START ---
          const drawControls = document.createElement('div');
          drawControls.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

          // Satellite button
          const satelliteButton = document.createElement('button');
          satelliteButton.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-satellite';
          satelliteButton.type = 'button';
          satelliteButton.title = 'Toggle Satellite View';
          satelliteButton.setAttribute('data-tooltip', 'Toggle Satellite View');
          satelliteButton.onclick = () => {
            if (!map.current || !map.current.loaded()) return;
            
            // grab whatever Mapbox thinks the visibility is right now
            const currentVis = map.current.getLayoutProperty('satellite-raster-layer', 'visibility');
            const newVis = (currentVis === 'visible') ? 'none' : 'visible';

            map.current.setLayoutProperty('satellite-raster-layer', 'visibility', newVis);
            satelliteButton.classList.toggle('active', newVis === 'visible');
            setIsSatellite(newVis === 'visible');
          };
          // render MUI icon into that button
          createRoot(satelliteButton).render(<SatelliteOutlinedIcon style={{ width: 24, height: 24 }} />);

          // Polygon button
          const polygonButton = document.createElement('button');
          polygonButton.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-polygon';
          polygonButton.type = 'button';
          polygonButton.title = 'Draw Polygon';
          polygonButton.setAttribute('data-tooltip', 'Draw Polygon');
          polygonButton.onclick = () => {
            const active = polygonButton.classList.toggle('active');
            if (active) {
              enterPolygonDrawModeRef.current?.();
            } else {
              resetDrawStateRef.current?.();
            }
          };
          createRoot(polygonButton).render(<CropFreeIcon style={{ width: 24, height: 24 }} />);

          // Freehand button
          const freehandButton = document.createElement('button');
          freehandButton.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-freehand';
          freehandButton.type = 'button';
          freehandButton.title = 'Draw Freehand';
          freehandButton.setAttribute('data-tooltip', 'Draw Freehand');
          freehandButton.onclick = () => {
            const active = freehandButton.classList.toggle('active');
            resetDrawStateRef.current?.();
            if (active) {
              freehandButton.classList.add('active');
              setIsFreehand(true);
              setIsDrawing(true);
              mapContainer.current?.classList.add('freehand-drawing');
              map.current.dragPan.disable();
              startFreehandRef.current?.();
            }
          };
          createRoot(freehandButton).render(
            <svg width={24} height={24} viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          );

          // Radius button
          const radiusButton = document.createElement('button');
          radiusButton.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-radius';
          radiusButton.type = 'button';
          radiusButton.title = 'Draw Radius';
          radiusButton.setAttribute('data-tooltip', 'Draw Radius');
          radiusButton.onclick = () => {
            const active = radiusButton.classList.toggle('active');
            if (active) {
              resetDrawStateRef.current?.();
              radiusButton.classList.add('active');
              setIsRadiusMode(true);
              setIsDrawing(true);
              mapContainer.current?.classList.add('radius-drawing');
              map.current.dragPan.disable();
              startRadiusRef.current?.();
            } else {
              resetDrawStateRef.current?.({
                clearRadius: true,
                clearFilter: true,
                refreshMap: true,
              });
            }
          };
          createRoot(radiusButton).render(<RadioButtonCheckedIcon style={{ width: 24, height: 24 }} />);

          // Clear button
          const clearButton = document.createElement('button');
          clearButton.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-clear';
          clearButton.type = 'button';
          clearButton.title = 'Clear All Drawings';
          clearButton.setAttribute('data-tooltip', 'Clear All Drawings');
          clearButton.onclick = () => {
            resetDrawStateRef.current?.({
              clearFeatures: true,
              clearRadius: true,
              clearFilter: true,
              refreshMap: true,
            });
          };
          createRoot(clearButton).render(<DeleteForeverRoundedIcon style={{ width: 24, height: 24 }} />);

          // put them all into one control and add to the map
          [ satelliteButton, polygonButton, freehandButton, radiusButton, clearButton ]
            .forEach(btn => drawControls.appendChild(btn));

          map.current.addControl({
            onAdd: () => drawControls,
            onRemove: () => drawControls.remove()
          }, 'top-left');
          // --- CUSTOM TOOLBAR END ---

          // Add subject layer
          map.current.addLayer({
            id: 'subject-layer',
            type: 'circle',
            source: 'comps',
            filter: ['==', 'isSubject', true],
            paint: getBasePaintProperties('subject-layer')
          });

          // Add subject sale indicator layer
          map.current.addLayer(getSubjectSaleIndicatorLayer());

          // Add selected comps layer
          map.current.addLayer({
            id: 'selected-comps-layer',
            type: 'circle',
            source: 'comps',
            filter: ['all', 
              ['==', 'selected', true],
              ['==', 'isSubject', false]
            ],
            paint: getBasePaintProperties('selected-comps-layer')
          });

          // Add reduction comps layer
          map.current.addLayer({
            id: 'reduction-comps-layer',
            type: 'circle',
            source: 'comps',
            filter: ['all', 
              ['==', 'selected', false],
              ['==', 'isSubject', false],
              ['==', 'supportsReduction', true]
            ],
            paint: getBasePaintProperties('reduction-comps-layer')
          });

          // Add no-reduction comps layer
          map.current.addLayer({
            id: 'no-reduction-comps-layer',
            type: 'circle',
            source: 'comps',
            filter: ['all', 
              ['==', 'selected', false],
              ['==', 'isSubject', false],
              ['==', 'supportsReduction', false]
            ],
            paint: getBasePaintProperties('no-reduction-comps-layer')
          });

          // Add selected comps text layer (shows row number or X if ALFlag === 0)
          map.current.addLayer(getSelectedCompsTextLayer(reportType));

          // Add unselected comps NAL text layer (shows red X when ALFlag === 0)
          // Skip this layer in assessment report type
          if (reportType !== 'assessment') {
            map.current.addLayer(getUnselectedCompsNALTextLayer());
          }

          // Add moved points layer
          map.current.addLayer(getMovedPointsLayer());

          // Add moved points text layer
          map.current.addLayer(getMovedPointsTextLayer());


          // Initialize the data
          updateCompData();
          
          // Add idle event handler to set loading to false
          map.current.on('idle', () => {
            setIsLoading(false);
          });
          
        } catch (error) {
          logger.error('Error in map load handler:', error);
          setIsLoading(false);
        }
      });

    } catch (error) {
      logger.error('Error initializing map:', error);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      logger.log('Cleaning up map instance');
      if (map.current) {
        try {
          if (map.current.getLayer('exclamation-layer')) {
            map.current.removeLayer('exclamation-layer');
          }
          map.current.remove();
        } catch (error) {
          logger.error('Error removing map:', error);
        }
        map.current = null;
      }
    };
  }, []);

  // Add styles for the custom controls
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .mapboxgl-ctrl-group {
        margin-bottom: 10px;
      }

      .mapboxgl-ctrl-group button {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: white;
        border: none;
        cursor: pointer;
        box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
        position: relative;
      }
      
      .mapboxgl-ctrl-group button:hover {
        background-color: #f8f8f8;
      }
      
      .mapboxgl-ctrl-group button svg {
        width: 20px;
        height: 20px;
      }
      
      .mapboxgl-ctrl-group button.active {
        background-color: #1976d2;
        color: white;
      }

      .mapboxgl-ctrl-group button.active:hover {
        background-color: #1976d2;
        color: white;
      }

      /* Tooltip styles */
      .mapboxgl-ctrl-group button[data-tooltip]:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        padding: 4px 8px;
        background-color: rgba(0, 0, 0, 0.8);
        color: white;
        font-size: 12px;
        border-radius: 4px;
        white-space: nowrap;
        pointer-events: none;
        margin-bottom: 4px;
      }

      /* Custom cursor for freehand drawing */
      .mapboxgl-canvas.pointer {
        cursor: pointer !important;
      }

      /* Freehand drawing cursor */
      .freehand-drawing {
        cursor: crosshair !important;
      }

      .freehand-drawing * {
        cursor: crosshair !important;
      }

      /* Polygon drawing cursor */
      .polygon-drawing {
        cursor: crosshair !important;
      }

      .polygon-drawing * {
        cursor: crosshair !important;
      }

      /* Hide the default draw controls */
      .mapboxgl-ctrl-group .mapboxgl-ctrl-draw {
        display: none;
      }

      /* Radius drawing cursor */
      .radius-drawing {
        cursor: crosshair !important;
      }

      .radius-drawing * {
        cursor: crosshair !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  // Update the draw.create event handler to remove duplicate filtering
  useEffect(() => {
    if (!map.current) return;

    const handleDrawCreate = (e) => {
      const feature = e.features[0];
      if (feature.geometry.type === 'Polygon') {
        const area = turf.area(feature);
        const center = turf.center(feature);
        
        onPolygonDraw({
          coordinates: feature.geometry.coordinates,
          area: area,
          center: center.geometry.coordinates
        });

        requestAnimationFrame(() => {
          updateCompData();
        });
      }

      // Draw is already leaving draw_polygon — avoid changeMode (nested onStop overflows)
      resetDrawState({ skipModeChange: true });
    };

    // Remove any existing handler
    map.current.off('draw.create');
    // Add new handler
    map.current.on('draw.create', handleDrawCreate);

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('draw.create', handleDrawCreate);
      }
    };
  }, [updateCompData, onPolygonDraw, resetDrawState]);

  // Add effect to handle draw events
  useEffect(() => {
    if (!map.current || !draw.current) return;

    const handleDrawEnd = () => {
      if (map.current?.getCanvas()) {
        map.current.getCanvas().style.cursor = '';
      }
      if (mapContainer.current) {
        mapContainer.current.classList.remove('polygon-drawing');
      }
    };

    const handleDrawUpdate = () => {
      if (map.current?.getCanvas()) {
        map.current.getCanvas().style.cursor = 'crosshair';
      }
    };

    const syncFilterAfterDelete = () => {
      const all = draw.current.getAll().features;
      // If no polygons remain, reset filter
      if (all.length === 0) {
        if (onMapFilterChange) {
          onMapFilterChange([]);
        }
        updateCompData();
      } else {
        // Get filtered parcel IDs from remaining features
        const filteredParcelIds = latLongs
          .filter(coord => {
            if (coord.isSubject) return true;
            const point = turf.point([coord.longitude, coord.latitude]);
            return all.some(polygon => 
              turf.booleanPointInPolygon(point, polygon)
            );
          })
          .map(coord => coord.parcel_id);

        // Update the filter
        if (onMapFilterChange) {
          onMapFilterChange(filteredParcelIds);
        }
        updateCompData();
      }
    };

    const handleDrawModeChange = (e) => {
      if (e.mode === 'draw_polygon') {
        map.current?.dragPan?.disable();
        if (map.current?.getCanvas()) {
          map.current.getCanvas().style.cursor = 'crosshair';
        }
        if (mapContainer.current) {
          mapContainer.current.classList.add('polygon-drawing');
        }
      } else {
        map.current?.dragPan?.enable();
        if (map.current?.getCanvas()) {
          map.current.getCanvas().style.cursor = '';
        }
        if (mapContainer.current) {
          mapContainer.current.classList.remove('polygon-drawing');
        }
      }
    };

    // Add draw event listeners
    map.current.on('draw.create', handleDrawEnd);
    map.current.on('draw.delete', (e) => {
      handleDrawEnd();
      syncFilterAfterDelete();
    });
    map.current.on('draw.update', (e) => {
      handleDrawUpdate();
      syncFilterAfterDelete();
    });
    map.current.on('draw.modechange', handleDrawModeChange);

    return () => {
      if (map.current) {
        map.current.off('draw.create', handleDrawEnd);
        map.current.off('draw.delete', handleDrawEnd);
        map.current.off('draw.update', handleDrawUpdate);
        map.current.off('draw.modechange', handleDrawModeChange);
      }
    };
  }, [updateCompData]);

  // Add effect to handle map centering when comp changes
  useEffect(() => {
    if (!map.current || !processedMapData.length) return;

    // Get all selected comps and subject
    const selectedComps = processedMapData.filter(coord => coord.selected || coord.isSubject);
    if (!selectedComps.length) return;

    // Create bounds object
    const bounds = new mapboxgl.LngLatBounds();

    // Extend bounds with all selected comps that have valid coordinates
    selectedComps.forEach(coord => {
      const lng = parseFloat(coord.longitude);
      const lat = parseFloat(coord.latitude);
      
      if (!isNaN(lng) && !isNaN(lat) && 
          lng >= -180 && lng <= 180 && 
          lat >= -90 && lat <= 90) {
        bounds.extend([lng, lat]);
      } 
    });

    // Only fit bounds if we have valid coordinates
    if (bounds.isEmpty()) {
      // If no valid coordinates, center on US
      map.current.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 4,
        duration: 3000
      });
    } else {
      // Add some padding to the bounds
      const padding = {
        top: 50,
        bottom: 50,
        left: 50,
        right: 50
      };

      // Fit map to bounds with padding
      map.current.fitBounds(bounds, {
        padding: padding,
        duration: 3000,
        maxZoom: 15 // Prevent zooming in too close
      });
    }
  }, [processedMapData]); // This will trigger when processedMapData changes due to comp changes

  // Add effect to handle map container width changes
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;

    // Add mouse move handler for cursor changes
    const handleMouseMove = (e) => {
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer']
      });
      
      if (features.length > 0) {
        map.current.getCanvas().style.cursor = 'pointer';
      } else {
        map.current.getCanvas().style.cursor = 'grab';
      }
    };

    map.current.on('mousemove', handleMouseMove);

    return () => {
      if (map.current) {
        map.current.off('mousemove', handleMouseMove);
      }
    };
  }, []);

  // Trigger a resize event when the property record card visibility changes
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;

    // Trigger a resize event when the property record card visibility changes
    map.current.resize();

    // If we're closing the property record card, wait for the transition to complete
    // before triggering another resize
    if (!selectedProperty) {
      const timer = setTimeout(() => {
        map.current.resize();
      }, 500); // Match the transition duration
      return () => clearTimeout(timer);
    }
  }, [selectedProperty]);

  // This has some duplicated code with other effects I believe?
  // Modify the effect to handle comp changes and property record visibility
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;

    const newSubjectCoord = latLongs.find(coord => coord.isSubject);

    // IMPORTANT: Do NOT clear selectedProperty when subject changes or comp changes
    // PropertyRecordCard should stay open regardless of subject/comp navigation
    // Always update the currentSubjectPid ref to latest subject
    if (newSubjectCoord) {
      currentSubjectPid.current = newSubjectCoord.parcel_id;
    }

    // Find the new subject point
    if (newSubjectCoord) {
      // Create bounds object
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([newSubjectCoord.longitude, newSubjectCoord.latitude]);

      // Add selected comps to bounds
      const selectedComps = processedMapData.filter(coord => coord.selected);
      selectedComps.forEach(coord => {
        bounds.extend([coord.longitude, coord.latitude]);
      });

      // Calculate padding based on whether property record is shown
      const padding = {
        top: 50,
        bottom: 50,
        left: 50,
        right: selectedProperty ? 150 : 50 // Extra padding on right when shifted
      };

      // Fit bounds with padding
      map.current.fitBounds(bounds, {
        padding: padding,
        duration: 1000,
        maxZoom: 15 // Changed from 12 to 15 to maintain consistent zoom level
      });
    }
  }, [processedMapData]); // Remove selectedProperty from dependencies

  // Update the click handler to only handle selection
  const handleClick = useCallback((e) => {
    if (!map.current) return;

    // Get the clicked point
    const features = map.current.queryRenderedFeatures(e.point, {
      layers: ['clusters', 'subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer']
    });

    if (features.length === 0) return;

    const clickedFeature = features[0];

    // Handle cluster click
    if (clickedFeature.properties?.cluster_id) {
      const clusterId = clickedFeature.properties.cluster_id;
      const pointCount = clickedFeature.properties.point_count;

      map.current.getSource('comps').getClusterLeaves(
        clusterId,
        pointCount,
        0,
        (err, leaves) => {
          if (err) return;

          const formattedLeaves = leaves.map(leaf => ({
            ...leaf.properties,
            longitude: leaf.geometry.coordinates[0],
            latitude: leaf.geometry.coordinates[1],
            id: leaf.properties.parcel_id
          }));

          const firstProperty = formattedLeaves[0];
          setSelectedProperty(firstProperty);
          setSelectedProperties(formattedLeaves);
          setCurrentPropertyIndex(0);
          setSelectedPointId(firstProperty.parcel_id);

          // Update cluster styling for only the selected cluster
          if (map.current.getLayer('clusters')) {
            map.current.setPaintProperty('clusters', 'circle-color', [
              'case',
              ['==', ['get', 'cluster_id'], clusterId],
              COLORS.WHITE,
              ['==', ['get', 'has_subject'], true], COLORS.SUBJECT,
              ['==', ['get', 'has_selected'], true], COLORS.SELECTED,
              ['==', ['get', 'all_green'], true], COLORS.REDUCTION,
              ['==', ['get', 'all_red'], true], COLORS.NO_REDUCTION,
              COLORS.MIXED
            ]);
            map.current.setPaintProperty('clusters', 'circle-stroke-color', [
              'case',
              ['==', ['get', 'cluster_id'], clusterId],
              COLORS.SELECTED,
              COLORS.BORDER
            ]);
            map.current.setPaintProperty('clusters', 'circle-stroke-width', [
              'case',
              ['==', ['get', 'cluster_id'], clusterId],
              STROKE_WIDTHS.MODIFIED,
              STROKE_WIDTHS.SUBJECT
            ]);
          }
        }
      );

      return;
    }

    // Handle regular point click
    const clickedPoint = clickedFeature.properties;

    // Reset all styling including clusters
    resetAllStyling(map.current, true);

    // Update selected point styling
    const layerId = clickedFeature.layer.id;
    if (map.current.getLayer(layerId)) {
      map.current.setPaintProperty(layerId, 'circle-color', [
        'case',
        ['==', ['get', 'parcel_id'], clickedPoint.parcel_id],
        COLORS.WHITE,
        layerId === 'subject-layer' ? COLORS.SUBJECT :
        layerId === 'selected-comps-layer' ? COLORS.SELECTED :
        layerId === 'reduction-comps-layer' ? COLORS.REDUCTION : COLORS.NO_REDUCTION
      ]);
      map.current.setPaintProperty(layerId, 'circle-stroke-color', [
        'case',
        ['==', ['get', 'parcel_id'], clickedPoint.parcel_id],
        COLORS.SELECTED,
        COLORS.BORDER
      ]);
      map.current.setPaintProperty(layerId, 'circle-stroke-width', [
        'case',
        ['==', ['get', 'parcel_id'], clickedPoint.parcel_id],
        STROKE_WIDTHS.MODIFIED,
        layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT :
        layerId === 'selected-comps-layer' ? STROKE_WIDTHS.SELECTED : STROKE_WIDTHS.DEFAULT
      ]);
    }

    // Update state
    setSelectedProperty(clickedPoint);
    setSelectedProperties([clickedPoint]);
    setCurrentPropertyIndex(0);
    setSelectedPointId(clickedPoint.parcel_id);
  }, [selectedComps]);

  // Add map click handler
  const handleMapClick = useCallback((e) => {
    if (!map.current) return;

    // Only proceed if we're not clicking on a point or cluster
    const features = map.current.queryRenderedFeatures(e.point, {
      layers: ['clusters', 'subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer']
    });

    if (features.length === 0) {
      // Reset all styling including clusters
      resetAllStyling(map.current, true);

      // Clear selection
      setSelectedProperty(null);
      setSelectedProperties([]);
      setCurrentPropertyIndex(0);
      setSelectedPointId(null);
    }
  }, []);

  const handleCancelClick = useCallback(() => {
    // Reset full dataset
    map.current.getSource('comps').setData({
      type: 'FeatureCollection',
      features: latLongs.map(coord => ({
        type: 'Feature',
        properties: { ...coord },
        geometry: {
          type: 'Point',
          coordinates: [coord.longitude, coord.latitude]
        }
      }))
    });

    setIsFixingLatLong(false);
    propertyBeingEditedRef.current = null;
    map.current.getCanvas().style.cursor = '';
    map.current.doubleClickZoom.enable();
    map.current.dragPan.enable();
    map.current.off('dblclick', handleDoubleClick);
    map.current.off('click', handleCancelClick);
    toast.dismiss(); // Dismiss any active toast
  }, [processedMapData]);

  const handleFixLatLong = useCallback((property) => {
    if (!property || !property.parcel_id) {
      logger.error('Invalid property object:', property);
      return;
    }

    logger.log('Starting fix lat/long for property:', property);

    // Ensure we have all required fields
    const propertyToEdit = {
      ...property,
      latitude: property.latitude || 0,
      longitude: property.longitude || 0,
      parcel_id: property.parcel_id,
      address: property.address || 'Unknown Address'
    };

    // Set the ref with the complete property object
    propertyBeingEditedRef.current = propertyToEdit;
    setIsFixingLatLong(true);
    
    // Clear selected property state to close the property record card
    setSelectedProperty(null);
    setSelectedProperties([]);
    setCurrentPropertyIndex(0);
    setSelectedPointId(null);

    // Create the feature for the single point
    const feature = {
      type: 'Feature',
      properties: { ...propertyToEdit },
      geometry: {
        type: 'Point',
        coordinates: [propertyToEdit.longitude, propertyToEdit.latitude]
      }
    };

    // Set the source data to only show this single point
    if (map.current && map.current.getSource('comps')) {
      logger.log('Setting initial point for editing:', feature);
    map.current.getSource('comps').setData({
      type: 'FeatureCollection',
      features: [feature]
    });
    }

    // Disable double-click zoom and enable crosshair cursor
    if (map.current) {
    map.current.doubleClickZoom.disable();
    map.current.getCanvas().style.cursor = 'crosshair';

      // Remove any existing handlers first
      map.current.off('dblclick');
      
      // Add the double-click handler
      map.current.on('dblclick', (e) => {
        logger.log('Double click detected:', e.lngLat);
        if (!propertyBeingEditedRef.current) return;
      
      const { lng, lat } = e.lngLat;
        const parcelId = propertyBeingEditedRef.current.parcel_id;
        const originalProperty = propertyBeingEditedRef.current;

        // Create updated property with new coordinates
        const updatedProperty = {
          ...originalProperty,
          longitude: lng,
          latitude: lat
        };

        // Store the moved point first
        setMovedPoints(prev => ({
          ...prev,
          [parcelId]: {
            ...updatedProperty,
            originalLatitude: originalProperty.latitude,
            originalLongitude: originalProperty.longitude,
            newLatitude: lat,
            newLongitude: lng
          }
        }));

        // Update latLongs array first
      const updatedLatLongs = latLongs.map(coord => {
          if (coord.parcel_id === parcelId) {
            return updatedProperty;
          }
          return coord;
        });

        // Create GeoJSON features from updated latLongs
        const features = updatedLatLongs.map(coord => ({
          type: 'Feature',
          id: coord.parcel_id, // Add explicit ID
          properties: {
            ...coord,
            isSubject: coord.isSubject,
            selected: coord.selected,
            supportsReduction: (() => {
              const subjectIFMV = latLongs[0]?.ifmv || 0;
              return coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false;
            })(),
            isModified: coord.parcel_id === parcelId
          },
          geometry: {
            type: 'Point',
            coordinates: [coord.longitude, coord.latitude]
          }
        }));

        // Update the map source with all points
        if (map.current && map.current.getSource('comps')) {
          // First update the source data
          map.current.getSource('comps').setData({
            type: 'FeatureCollection',
            features: features
          });

          // Wait for the source to update
          map.current.once('sourcedata', () => {
            // Update point styling for modified points
            ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'].forEach(layerId => {
              if (map.current.getLayer(layerId)) {
                map.current.setPaintProperty(layerId, 'circle-stroke-color', [
          'case',
                  ['==', ['get', 'parcel_id'], parcelId],
                  '#FFD700', // Yellow border for modified points
                  '#FFFFFF'
                ]);
                
                map.current.setPaintProperty(layerId, 'circle-stroke-width', [
                  'case',
                  ['==', ['get', 'parcel_id'], parcelId],
                  3, // Thicker border for modified points
                  layerId === 'subject-layer' ? 2 : 1
                ]);
              }
            });

            // Add or update the exclamation mark layer
            if (!map.current.getLayer('exclamation-layer')) {
              map.current.addLayer({
                id: 'exclamation-layer',
                type: 'symbol',
                source: 'comps',
                layout: {
                  'text-field': '!',
                  'text-size': 12,
                  'text-allow-overlap': true,
                  'text-ignore-placement': true,
                  'text-offset': [0, 0],
                  'text-anchor': 'center'
                },
                paint: {
                  'text-color': '#ef4444', // Red exclamation mark
                  'text-halo-color': '#FFFFFF',
                  'text-halo-width': 1
                },
                filter: ['==', ['get', 'parcel_id'], parcelId]
              });
            } else {
              map.current.setFilter('exclamation-layer', ['==', ['get', 'parcel_id'], parcelId]);
            }
          });
      }

      // Cleanup
      setIsFixingLatLong(false);
      propertyBeingEditedRef.current = null;
      map.current.getCanvas().style.cursor = '';
      map.current.doubleClickZoom.enable();
        map.current.dragPan.enable();
        map.current.off('dblclick');
      map.current.off('click', handleCancelClick);
      
        // Show success message
        toast.dismiss();

        // Force a re-render of the map data
        updateCompData();
        });
      }

    // Create a toast with a cancel button
    const toastId = toast.info(
      <div>
        Double click to update location for {propertyToEdit.address} or{' '}
        <button
          onClick={() => {
            handleCancelClick();
            toast.dismiss(toastId);
          }}
          style={{ 
            color: '#1976d2', 
            textDecoration: 'underline',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            margin: 0
          }}
        >
          click here to cancel update
        </button>
      </div>,
      { 
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        containerId: "edit-mode-toast"
      }
    );

    // Store the toast ID in the ref for cleanup
    propertyBeingEditedRef.current.toastId = toastId;
  }, [processedMapData, handleCancelClick, updateCompData]);

  // Add effect to handle radius circle persistence
  useEffect(() => {
    if (!map.current || !map.current.loaded() || !radiusCircle) return;

    // Add radius circle source and layer if they don't exist
    if (!map.current.getSource('radius-circle')) {
      map.current.addSource('radius-circle', {
        type: 'geojson',
        data: radiusCircle.circle
      });

      map.current.addLayer({
        id: 'radius-circle-layer',
        type: 'fill',
        source: 'radius-circle',
        paint: {
          'fill-color': '#1976d2',
          'fill-opacity': 0.1,
          'fill-outline-color': '#1976d2'
        }
      });
    } else {
      // Update existing circle
      map.current.getSource('radius-circle').setData(radiusCircle.circle);
    }

    // Filter points within radius
    const filteredParcelIds = latLongs
      .filter(coord => {
        if (coord.isSubject) return true;
        const distance = turf.distance(
          turf.point([coord.longitude, coord.latitude]),
          turf.point(radiusCircle.center),
          { units: 'miles' }
        );
        return distance <= radiusCircle.radius;
      })
      .map(coord => coord.parcel_id);

    // Call the callback with filtered parcel IDs
    if (onMapFilterChange) {
      onMapFilterChange(filteredParcelIds);
    }

    // Update the map data
    updateCompData();

    return () => {
      if (map.current) {
        if (map.current.getLayer('radius-circle-layer')) {
          map.current.removeLayer('radius-circle-layer');
        }
        if (map.current.getSource('radius-circle')) {
          map.current.removeSource('radius-circle');
        }
      }
    };
  }, [radiusCircle, latLongs, onMapFilterChange, updateCompData]);

  // Add this after the other components
  const LocationConfirmationDialog = ({ open, onClose, movedPoints, onConfirm }) => {
    return (
      <StableMuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Confirm Location Updates</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Address</TableCell>
                <TableCell>Original Location</TableCell>
                <TableCell>New Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.values(movedPoints).map((point) => (
                <TableRow key={point.parcel_id}>
                  <TableCell>{point.address}</TableCell>
                  <TableCell>
                    {point.originalLatitude.toFixed(6)}, {point.originalLongitude.toFixed(6)}
                  </TableCell>
                  <TableCell>
                    {point.newLatitude.toFixed(6)}, {point.newLongitude.toFixed(6)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} variant="contained" color="primary">
            Confirm Changes
          </Button>
        </DialogActions>
      </StableMuiDialog>
    );
  };

  // Add this before the return statement in PropertyMap
  const LocationConfirmationOverlay = ({ movedPoints, onConfirm }) => {
    if (Object.values(movedPoints).length === 0 || selectedProperty) return null;
    
    return (
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 100,
          zIndex: '0',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: 2,
          borderRadius: 1,
          boxShadow: 3,
          display: 'flex',
          alignItems: 'center',
          zIndex: 10000,
          gap: 2,
          pointerEvents: 'auto'
        }}
      >
        <Typography>
          {Object.values(movedPoints).length} location{Object.values(movedPoints).length > 1 ? 's' : ''} updated
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            setMovedPoints({});
            setShowConfirmDialog(false);
            // Reset point styling to remove yellow borders
            ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'].forEach(layerId => {
              if (map.current && map.current.getLayer(layerId)) {
                map.current.setPaintProperty(layerId, 'circle-stroke-color', COLORS.BORDER);
                map.current.setPaintProperty(layerId, 'circle-stroke-width', 
                  layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT : STROKE_WIDTHS.DEFAULT
                );
              }
            });
            // Remove exclamation layer
            if (map.current && map.current.getLayer('exclamation-layer')) {
              map.current.removeLayer('exclamation-layer');
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            logger.log('Confirmed moved points:', movedPoints);
            onConfirm(movedPoints);
          }}
        >
          Confirm
        </Button>
      </Box>
    );
  };

  const startRadius = useCallback(() => {
    const mapInstance = map.current;
    // Don't gate on map.loaded() — after comp updates it can be false while the map is interactive
    if (!mapInstance?.getCanvas?.()) return;

    if (mapInstance.radiusHandlers) {
      const {
        handleMouseMove,
        handleClick,
        handleRadiusDrag,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseDown,
      } = mapInstance.radiusHandlers;
      mapInstance.off('mousemove', handleMouseMove);
      mapInstance.off('click', handleClick);
      mapInstance.off('mousemove', handleRadiusDrag);
      mapInstance.off('mouseenter', 'radius-border-layer', handleMouseEnter);
      mapInstance.off('mouseleave', 'radius-border-layer', handleMouseLeave);
      mapInstance.off('mousedown', 'radius-border-layer', handleMouseDown);
      mapInstance.radiusHandlers = null;
    }

    activeDrawToolRef.current = 'radius';
    mapInstance.getCanvas().style.cursor = 'crosshair';

    // Add radius circle source and layer if they don't exist
    if (!mapInstance.getSource('radius-circle')) {
      mapInstance.addSource('radius-circle', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [[]]
          }
        }
      });

      // Add radius circle fill layer
      mapInstance.addLayer({
        id: 'radius-circle-layer',
        type: 'fill',
        source: 'radius-circle',
        paint: {
          'fill-color': '#1976d2',
          'fill-opacity': 0.1
        }
      });

      // Add radius border layer
      mapInstance.addLayer({
        id: 'radius-border-layer',
        type: 'line',
        source: 'radius-circle',
        paint: {
          'line-color': '#1976d2',
          'line-width': 4,
          'line-opacity': 1
        }
      });

      // Add radius line source and layer
      mapInstance.addSource('radius-line', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      });

      mapInstance.addLayer({
        id: 'radius-line-layer',
        type: 'line',
        source: 'radius-line',
        paint: {
          'line-color': '#1976d2',
          'line-width': 2,
          'line-dasharray': [2, 2]
        }
      });

      // Add radius label source and layer
      mapInstance.addSource('radius-label', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: []
          }
        }
      });

      mapInstance.addLayer({
        id: 'radius-label-layer',
        type: 'symbol',
        source: 'radius-label',
        layout: {
          'text-field': ['concat', ['to-string', ['get', 'radius']], ' miles'],
          'text-size': 12,
          'text-font': ['Open Sans Bold'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'text-anchor': 'center',
          'text-offset': [0, -1]
        },
        paint: {
          'text-color': '#1976d2',
          'text-halo-color': '#FFFFFF',
          'text-halo-width': 1
        }
      });
    }

    // Initialize radiusCircleRef with current subject coordinates
    const currentLatLongs = mapInstance.getSource('comps')._data.features.map(f => ({
      ...f.properties,
      longitude: f.geometry.coordinates[0],
      latitude: f.geometry.coordinates[1]
    }));
    
    const subjectCoord = currentLatLongs.find(coord => coord.isSubject);
    if (!subjectCoord) {
      toast.error('No subject property found');
      return;
    }

    radiusCircleRef.current = {
      center: [subjectCoord.longitude, subjectCoord.latitude],
      radius: 0
    };

    // Mouse move updates radius preview
    const handleMouseMove = (e) => {
      if (!radiusCircleRef.current) return;
      
      const center = radiusCircleRef.current.center;
      const radius = turf.distance(
        turf.point(center),
        turf.point([e.lngLat.lng, e.lngLat.lat]),
        { units: 'miles' }
      );

      // Update radius circle
      radiusCircleRef.current.radius = radius;

      // Update the circle on the map
      if (mapInstance.getSource('radius-circle')) {
        const circle = turf.circle(center, radius, { units: 'miles', steps: 64 });
        mapInstance.getSource('radius-circle').setData(circle);
      }

      // Update radius line
      if (mapInstance.getSource('radius-line')) {
        const line = turf.lineString([center, [e.lngLat.lng, e.lngLat.lat]]);
        mapInstance.getSource('radius-line').setData(line);
      }

      // Update radius label
      if (mapInstance.getSource('radius-label')) {
        const midPoint = turf.midpoint(
          turf.point(center),
          turf.point([e.lngLat.lng, e.lngLat.lat])
        );
        mapInstance.getSource('radius-label').setData({
          type: 'Feature',
          properties: {
            radius: Math.round(radius * 10) / 10
          },
          geometry: {
            type: 'Point',
            coordinates: midPoint.geometry.coordinates
          }
        });
      }
    };

    // Add drag handler for radius border
    const handleRadiusDrag = (() => {
      let lastUpdate = 0;
      const THROTTLE_MS = 16; // ~60fps for visual updates

      return (e) => {
        if (!radiusCircleRef.current || !e.lngLat) return;

        const now = Date.now();
        const center = radiusCircleRef.current.center;
        const radius = turf.distance(
          turf.point(center),
          turf.point([e.lngLat.lng, e.lngLat.lat]),
          { units: 'miles' }
        );

        // Update radius circle ref immediately for smooth dragging
        radiusCircleRef.current.radius = radius;

        // Throttle visual updates for smooth rendering
        if (now - lastUpdate >= THROTTLE_MS) {
          // Update the circle on the map
          if (mapInstance.getSource('radius-circle')) {
            const circle = turf.circle(center, radius, { units: 'miles', steps: 64 });
            mapInstance.getSource('radius-circle').setData(circle);
          }

          // Update radius line
          if (mapInstance.getSource('radius-line')) {
            const line = turf.lineString([center, [e.lngLat.lng, e.lngLat.lat]]);
            mapInstance.getSource('radius-line').setData(line);
          }

          // Update radius label
          if (mapInstance.getSource('radius-label')) {
            const midPoint = turf.midpoint(
              turf.point(center),
              turf.point([e.lngLat.lng, e.lngLat.lat])
            );
            mapInstance.getSource('radius-label').setData({
              type: 'Feature',
              properties: {
                radius: Math.round(radius * 10) / 10
              },
              geometry: {
                type: 'Point',
                coordinates: midPoint.geometry.coordinates
              }
            });
          }

          lastUpdate = now;
        }
      };
    })();

    const handleClick = (e) => {
      // Get fresh subject and latLongs every time using updateCompDataRef
      const currentLatLongs = mapInstance.getSource('comps')._data.features.map(f => ({
        ...f.properties,
        longitude: f.geometry.coordinates[0],
        latitude: f.geometry.coordinates[1]
      }));
      
      const subjectCoord = currentLatLongs.find(coord => coord.isSubject);
      if (!subjectCoord) {
        toast.error('No subject property found');
        return;
      }

      const center = [subjectCoord.longitude, subjectCoord.latitude];
      const radius = turf.distance(
        turf.point(center),
        turf.point([e.lngLat.lng, e.lngLat.lat]),
        { units: 'miles' }
      );

      // Create the final circle
      const circle = turf.circle(center, radius, { units: 'miles', steps: 64 });

      // Store the circle in state
      setRadiusCircle({
        center,
        radius,
        circle
      });
      radiusCircleRef.current = { center, radius };

      // Update the map data using the ref
      if (updateCompDataRef.current) {
        updateCompDataRef.current();
      } else {
        updateCompData();
      }

      // Exit placement mode; keep circle + border-resize handlers
      setIsRadiusMode(false);
      setIsDrawing(false);
      activeDrawToolRef.current = null;
      mapContainer.current?.classList.remove('radius-drawing');
      document.querySelector('.mapboxgl-ctrl-radius')?.classList.remove('active');
      mapInstance.dragPan.enable();
      mapInstance.getCanvas().style.cursor = '';
      mapInstance.off('mousemove', handleMouseMove);
      mapInstance.off('click', handleClick);
      mapInstance.radiusHandlers = {
        handleMouseMove: () => {},
        handleClick: () => {},
        handleRadiusDrag,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseDown,
      };
    };

    const handleMouseDown = function(e) {
      e.preventDefault();
      mapInstance.dragPan.disable();
      mapInstance.on('mousemove', handleRadiusDrag);
      mapInstance.once('mouseup', function() {
        mapInstance.dragPan.enable();
        mapInstance.off('mousemove', handleRadiusDrag);

        // Get the final radius from the ref
        const center = radiusCircleRef.current.center;
        const radius = radiusCircleRef.current.radius;

        // Create the final circle
        const circle = turf.circle(center, radius, { units: 'miles', steps: 64 });

        // Update the state with final values
        setRadiusCircle({
          center,
          radius,
          circle
        });
      });
    };

    // Define mouse enter/leave handlers
    const handleMouseEnter = function() {
      mapInstance.getCanvas().style.cursor = 'move';
    };

    const handleMouseLeave = function() {
      mapInstance.getCanvas().style.cursor = '';
    };

    // Store handlers for cleanup
    mapInstance.radiusHandlers = {
      handleMouseMove,
      handleClick,
      handleRadiusDrag,
      handleMouseEnter,
      handleMouseLeave,
      handleMouseDown
    };

    // Add the event handlers
    mapInstance.on('mousemove', handleMouseMove);
    mapInstance.on('click', handleClick);

    // Add mouseover handler for radius border
    mapInstance.on('mouseenter', 'radius-border-layer', handleMouseEnter);
    mapInstance.on('mouseleave', 'radius-border-layer', handleMouseLeave);
    mapInstance.on('mousedown', 'radius-border-layer', handleMouseDown);

    // Add cleanup for radius layers
    return () => {
      if (mapInstance.getLayer('radius-border-layer')) {
        mapInstance.removeLayer('radius-border-layer');
      }
      if (mapInstance.getSource('radius-circle')) {
        mapInstance.removeSource('radius-circle');
      }
      if (mapInstance.getLayer('radius-line-layer')) {
        mapInstance.removeLayer('radius-line-layer');
      }
      if (mapInstance.getSource('radius-line')) {
        mapInstance.removeSource('radius-line');
      }
      if (mapInstance.getLayer('radius-label-layer')) {
        mapInstance.removeLayer('radius-label-layer');
      }
      if (mapInstance.getSource('radius-label')) {
        mapInstance.removeSource('radius-label');
      }
      radiusCircleRef.current = null;
      setRadiusCircle(null);
    };
  }, [onMapFilterChange, updateCompDataRef]);

  useEffect(() => {
    startRadiusRef.current = startRadius;
  }, [startRadius]);

  // Add back the getTooltipHTML function
  const getTooltipHTML = useCallback((features, lngLat) => {
    if (!features || features.length === 0) return null;
    // Get subject IFMV from latLongs[0]
    const subjectIFMV = latLongs[0]?.ifmv || 0;
    // Get all features at this point
    const allFeatures = features.reduce((acc, feature) => {
      const overlaps = feature.properties.allOverlaps;
      if (overlaps && Array.isArray(overlaps) && overlaps.length > 0) {
        return [...acc, ...overlaps.map(overlap => ({
          ...overlap,
          id: overlap.parcel_id
        }))];
      }
      return [...acc, {
        ...feature.properties,
        id: feature.properties.parcel_id
      }];
    }, []);
    // Filter out the currently selected point
    const filteredFeatures = allFeatures.filter(f => f.parcel_id !== selectedPointId);
    if (filteredFeatures.length > 1) {
      return buildMultiTooltipHTML(filteredFeatures, isDarkMode, reportType);
    }
    // Otherwise show the single tooltip, always passing subjectIFMV
    return buildSingleTooltipHTML(filteredFeatures[0], isDarkMode, subjectIFMV, reportType);
  }, [selectedPointId, isDarkMode, latLongs, reportType]);

  // Add back the handleCompSelect function
  const handleCompSelect = useCallback((parcelId) => {
    if (updateSelectedComps && selectedComps) {
      const newSelection = selectedComps.includes(parcelId)
        ? selectedComps.filter(id => id !== parcelId)
        : [...selectedComps, parcelId];
      updateSelectedComps(newSelection, true); // Pass true to prevent resetting compDisplay
    }
  }, [updateSelectedComps, selectedComps]);

  // Add back the handleDoubleClick function
  const handleDoubleClick = useCallback((e) => {
    if (e.originalEvent) {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
    }

    if (!e || !e.lngLat || !propertyBeingEditedRef.current) {
      logger.warn('Invalid event or missing property being edited');
      return;
    }

    const property = propertyBeingEditedRef.current;
    if (!property || !property.parcel_id || typeof property.latitude === 'undefined' || typeof property.longitude === 'undefined') {
      logger.warn('Invalid property data:', property);
      return;
    }
    
    const { lng, lat } = e.lngLat;
    const parcelId = property.parcel_id;
    
    try {
      // Store the moved point
      setMovedPoints(prev => ({
        ...prev,
        [parcelId]: {
          ...property,
          originalLatitude: property.latitude,
          originalLongitude: property.longitude,
          newLatitude: lat,
          newLongitude: lng
        }
      }));

      // Update the point's coordinates in latLongs
      const updatedLatLongs = latLongs.map(coord => {
        if (coord.parcel_id === parcelId) {
          return {
            ...coord,
            longitude: lng,
            latitude: lat
          };
        }
        return coord;
      });

      // Update the map source with the new coordinates
      if (map.current && map.current.getSource('comps')) {
        const newFeatures = updatedLatLongs.map(coord => ({
          type: 'Feature',
          properties: {
            ...coord,
            isModified: Object.keys(movedPoints).includes(coord.parcel_id)
          },
          geometry: {
            type: 'Point',
            coordinates: [coord.longitude, coord.latitude]
          }
        }));

        map.current.getSource('comps').setData({
          type: 'FeatureCollection',
          features: newFeatures
        });
      }

      // Cleanup
      setIsFixingLatLong(false);
      propertyBeingEditedRef.current = null;
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
        map.current.doubleClickZoom.enable();
        map.current.dragPan.enable();
        map.current.off('dblclick', handleDoubleClick);
        map.current.off('click', handleCancelClick);
      }
      toast.dismiss();
    } catch (error) {
      logger.error('Error updating location:', error);
      toast.error("Failed to update location. Please try again.");
      // Reset state in case of error
      setIsFixingLatLong(false);
      propertyBeingEditedRef.current = null;
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
        map.current.doubleClickZoom.enable();
        map.current.dragPan.enable();
      }
    }
  }, [processedMapData, movedPoints]);

  // Add event listeners
  useEffect(() => {
    if (!map.current) return;

    map.current.on('click', handleClick);
    map.current.on('mousedown', handleMapClick);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      if (map.current) {
        map.current.off('click', handleClick);
        map.current.off('mousedown', handleMapClick);
      }
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleClick, handleMapClick, handleKeyDown]);

  return (
    <div className="relative w-full h-full">
      <style>
        {`
          .custom-popup {
            max-height: 80vh;
            z-index: 9999 !important;
          }
          .custom-popup .mapboxgl-popup-content {
            max-height: 300px;
            overflow-y: auto;
            cursor: default;
            padding: 10px;
            border-radius: 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          .custom-popup .mapboxgl-popup-close-button {
            font-size: 20px;
            padding: 8px;
            color: #DC2626;
            background: none;
            border: none;
            cursor: pointer;
            opacity: 0;
            transition: opacity 0.2s;
          }
          .custom-popup:hover .mapboxgl-popup-close-button {
            opacity: 1;
          }
          .custom-popup .mapboxgl-popup-close-button:hover {
            background: none;
            color: #991B1B;
          }
          .custom-popup .mapboxgl-popup-tip {
            border-top-color: rgba(0, 0, 0, 0.2);
            border-bottom-color: rgba(0, 0, 0, 0.2);
            width: 20px;
            height: 10px;
          }
          .map-container {
            width: 100%;
            height: 100%;
            overflow: visible !important;
            transition: transform 0.3s ease-out;
            will-change: transform;
            z-index: 99 !important;
          }
          .map-container.shifted {
            transform: translateX(-25%);
          }

        `}
      </style>
      <div 
        ref={mapContainer} 
        className="map-container"
      />
      
      <HoverTooltip
        map={map.current}
        layerIds={[
          'subject-layer',
          'selected-comps-layer',
          'reduction-comps-layer',
          'no-reduction-comps-layer',
          'clusters'
        ]}
        getHTML={(features, lngLat) => {
          // If it's a cluster, get the leaves
          if (features[0]?.properties?.cluster_id) {
            const clusterId = features[0].properties.cluster_id;
            const pointCount = features[0].properties.point_count;
            
            // Return a promise that resolves with the tooltip HTML
            return new Promise((resolve) => {
              map.current.getSource('comps').getClusterLeaves(
                clusterId,
                pointCount,
                0,
                (err, leaves) => {
                  if (err) {
                    resolve(null);
                    return;
                  }
                  
                  // Format the leaves to match our existing tooltip format
                  const formattedLeaves = leaves.map(leaf => ({
                    ...leaf.properties,
                    longitude: leaf.geometry.coordinates[0],
                    latitude: leaf.geometry.coordinates[1],
                    id: leaf.properties.parcel_id
                  }));

                  // Use the same tooltip builder as our regular multi-point tooltips
                  resolve(buildMultiTooltipHTML(formattedLeaves, isDarkMode, reportType));
                }
              );
            });
          }
          return getTooltipHTML(features, lngLat);
        }}
        onCompSelect={handleCompSelect}
        isDrawing={isDrawing || isFreehand || isRadiusMode}
        latLongs={latLongs} // Add latLongs prop
      />
      
      {/* Property Record Card Overlay */}
      {selectedProperty && mapContainerRect && (
        <OverlayPortal>
          <div 
            className="property-record-card" 
            style={{ 
              position: 'fixed', 
              top: `${mapContainerRect.top}px`, 
              left: `${mapContainerRect.left + mapContainerRect.width/2}px`, 
              height: `${mapContainerRect.height}px`, 
              width: `${mapContainerRect.width/2}px`, 
              zIndex: 1100,
              pointerEvents: 'none'
            }}
          >
            <div style={{ pointerEvents: 'auto', height: '100%', width: '100%' }}>
              <PropertyRecordCard
                selectedProperty={selectedProperty}
                selectedProperties={selectedProperties}
                currentPropertyIndex={currentPropertyIndex}
                handleCompViewClose={handleCompViewClose}
                onClose={() => {
                  // Reset point styling
                  ['subject-layer', 'selected-comps-layer', 'reduction-comps-layer', 'no-reduction-comps-layer'].forEach(layerId => {
                    if (map.current.getLayer(layerId)) {
                      map.current.setPaintProperty(layerId, 'circle-color', 
                        layerId === 'subject-layer' ? COLORS.SUBJECT :
                        layerId === 'selected-comps-layer' ? COLORS.SELECTED :
                        layerId === 'reduction-comps-layer' ? COLORS.REDUCTION : COLORS.NO_REDUCTION
                      );
                      map.current.setPaintProperty(layerId, 'circle-stroke-color', COLORS.BORDER);
                      map.current.setPaintProperty(layerId, 'circle-stroke-width', 
                        layerId === 'subject-layer' ? STROKE_WIDTHS.SUBJECT : STROKE_WIDTHS.DEFAULT
                      );
                    }
                  });

                  // Reset cluster styling
                  if (map.current.getLayer('clusters')) {
                    const clusterProperties = getClusterPaintProperties();
                    Object.entries(clusterProperties).forEach(([property, value]) => {
                      map.current.setPaintProperty('clusters', property, value);
                    });
                  }

                  setSelectedProperty(null);
                  setSelectedProperties([]);
                  setCurrentPropertyIndex(0);
                  setSelectedPointId(null);
                  if(externalSelectedProperty) {
                    handleCompViewClose();
                  }
                }}
                onNext={() => {
                  if (currentPropertyIndex < selectedProperties.length - 1) {
                    setCurrentPropertyIndex(currentPropertyIndex + 1);
                    setSelectedProperty(selectedProperties[currentPropertyIndex + 1]);
                  }
                }}
                onPrevious={() => {
                  if (currentPropertyIndex > 0) {
                    setCurrentPropertyIndex(currentPropertyIndex - 1);
                    setSelectedProperty(selectedProperties[currentPropertyIndex - 1]);
                  }
                }}
                handleProcessDBUpdate={handleProcessDBUpdate}
                selectedComps={selectedComps}
                handleCompSelect={handleCompSelect}
                caseObject={caseObject}
                onFixLatLong={handleFixLatLong}
                map={map.current}
                isSaleRun={isSaleRun}
              />
            </div>
          </div>
        </OverlayPortal>
      )}
      
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-[999999]">
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-700 font-medium">Loading Map...</span>
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="absolute bottom-2 left-2 z-[200] bg-white p-2 rounded shadow-md" style={{ minWidth: '140px' }}>
          <div
            className="flex items-center cursor-pointer select-none mb-1"
            onClick={() => setLegendCollapsed((prev) => !prev)}
            style={{ userSelect: 'none' }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              style={{
                transition: 'transform 0.2s',
                transform: legendCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                marginRight: 6
              }}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M6 8l4 4 4-4" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-xs font-medium">Map Legend</span>
          </div>
          {!legendCollapsed && (
            <>
              <div className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded transition-opacity legend-item" style={{ opacity: 1, minWidth: '140px' }} onClick={() => {
                const features = latLongs.map(coord => ({
                  type: 'Feature',
                  id: coord.parcel_id,
                  properties: {
                    ...coord,
                    isSubject: coord.isSubject,
                    selected: coord.selected,
                    supportsReduction: (() => {
                      const subjectIFMV = latLongs[0]?.ifmv || 0;
                      return coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false;
                    })(),
                    isModified: false
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [coord.longitude, coord.latitude]
                  }
                }));

                const currentFeatures = map.current.getSource('comps')._data.features;
                if (currentFeatures.length === 1 && currentFeatures[0].properties.isSubject) {
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: features
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    item.style.opacity = '1';
                    const textSpan = item.querySelector('.text-xs');
                    textSpan.textContent = textSpan.getAttribute('data-original-text');
                    textSpan.classList.remove('text-red-500');
                  });
                } else {
                  // Always include subject point in filtered results
                  const subjectFeature = features.find(f => f.properties.isSubject);
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: [subjectFeature]
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    const textSpan = item.querySelector('.text-xs');
                    const originalText = textSpan.getAttribute('data-original-text');
                    item.style.opacity = originalText === 'Subject Property' ? '1' : '0.4';
                    if (originalText === 'Subject Property') {
                      textSpan.textContent = 'Remove Filter';
                      textSpan.classList.add('text-red-500');
                    } else {
                      textSpan.textContent = originalText;
                      textSpan.classList.remove('text-red-500');
                    }
                  });
                }
              }} onMouseEnter={(e) => {
                const subjectCurrentFeatures = map.current.getSource('comps')._data.features;
                if (subjectCurrentFeatures.length === 1 && subjectCurrentFeatures[0].properties.isSubject) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Remove Filter';
                  textSpan.classList.add('text-red-500');
                }
              }} onMouseLeave={(e) => {
                const subjectCurrentFeatures = map.current.getSource('comps')._data.features;
                if (subjectCurrentFeatures.length === 1 && subjectCurrentFeatures[0].properties.isSubject) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Subject Property';
                  textSpan.classList.remove('text-red-500');
                }
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#FFD700', border: '1px solid white', marginRight: '4px' }}></div>
                <span className="text-xs" data-original-text="Subject Property">Subject Property</span>
              </div>
              <div className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded transition-opacity legend-item" style={{ opacity: 1, minWidth: '140px' }} onClick={() => {
                const features = latLongs.map(coord => ({
                  type: 'Feature',
                  id: coord.parcel_id,
                  properties: {
                    ...coord,
                    isSubject: coord.isSubject,
                    selected: coord.selected,
                    supportsReduction: (() => {
                      const subjectIFMV = latLongs[0]?.ifmv || 0;
                      return coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false;
                    })(),
                    isModified: false
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [coord.longitude, coord.latitude]
                  }
                }));

                const currentFeatures = map.current.getSource('comps')._data.features;
                if (currentFeatures.length === features.filter(f => f.properties.selected).length + 1) { // +1 for subject
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: features
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    item.style.opacity = '1';
                    const textSpan = item.querySelector('.text-xs');
                    textSpan.textContent = textSpan.getAttribute('data-original-text');
                    textSpan.classList.remove('text-red-500');
                  });
                } else {
                  // Always include subject point in filtered results
                  const subjectFeature = features.find(f => f.properties.isSubject);
                  const selectedFeatures = features.filter(f => f.properties.selected);
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: [subjectFeature, ...selectedFeatures]
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    const textSpan = item.querySelector('.text-xs');
                    const originalText = textSpan.getAttribute('data-original-text');
                    item.style.opacity = originalText === 'Selected Comps' ? '1' : '0.4';
                    if (originalText === 'Selected Comps') {
                      textSpan.textContent = 'Remove Filter';
                      textSpan.classList.add('text-red-500');
                    } else {
                      textSpan.textContent = originalText;
                      textSpan.classList.remove('text-red-500');
                    }
                  });
                }
              }} onMouseEnter={(e) => {
                const selectedCurrentFeatures = map.current.getSource('comps')._data.features;
                const selectedFeatures = processedMapData.filter(coord => coord.selected);
                if (selectedCurrentFeatures.length === selectedFeatures.length) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Remove Filter';
                  textSpan.classList.add('text-red-500');
                }
              }} onMouseLeave={(e) => {
                const selectedCurrentFeatures = map.current.getSource('comps')._data.features;
                const selectedFeatures = processedMapData.filter(coord => coord.selected);
                if (selectedCurrentFeatures.length === selectedFeatures.length) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Selected Comps';
                  textSpan.classList.remove('text-red-500');
                }
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#4169E1', border: '1px solid white', marginRight: '4px' }}></div>
                <span className="text-xs" data-original-text="Selected Comps">Selected Comps</span>
              </div>
              <div className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded transition-opacity legend-item" style={{ opacity: 1, minWidth: '140px' }} onClick={() => {
                const features = latLongs.map(coord => ({
                  type: 'Feature',
                  id: coord.parcel_id,
                  properties: {
                    ...coord,
                    isSubject: coord.isSubject,
                    selected: coord.selected,
                    supportsReduction: (() => {
                      const subjectIFMV = latLongs[0]?.ifmv || 0;
                      return coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false;
                    })(),
                    isModified: false
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [coord.longitude, coord.latitude]
                  }
                }));

                const currentFeatures = map.current.getSource('comps')._data.features;
                if (currentFeatures.length === features.filter(f => f.properties.supportsReduction).length + 1) { // +1 for subject
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: features
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    item.style.opacity = '1';
                    const textSpan = item.querySelector('.text-xs');
                    textSpan.textContent = textSpan.getAttribute('data-original-text');
                    textSpan.classList.remove('text-red-500');
                  });
                } else {
                  // Always include subject point in filtered results
                  const subjectFeature = features.find(f => f.properties.isSubject);
                  const reductionFeatures = features.filter(f => f.properties.supportsReduction);
                  map.current.getSource('comps').setData({
                    type: 'FeatureCollection',
                    features: [subjectFeature, ...reductionFeatures]
                  });
                  document.querySelectorAll('.legend-item').forEach(item => {
                    const textSpan = item.querySelector('.text-xs');
                    const originalText = textSpan.getAttribute('data-original-text');
                    item.style.opacity = originalText === 'Supports Reduction' ? '1' : '0.4';
                    if (originalText === 'Supports Reduction') {
                      textSpan.textContent = 'Remove Filter';
                      textSpan.classList.add('text-red-500');
                    } else {
                      textSpan.textContent = originalText;
                      textSpan.classList.remove('text-red-500');
                    }
                  });
                }
              }} onMouseEnter={(e) => {
                const reductionCurrentFeatures = map.current.getSource('comps')._data.features;
                const subjectIFMV = latLongs[0]?.ifmv || 0;
                const reductionFeatures = latLongs.filter(coord => coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false);
                if (reductionCurrentFeatures.length === reductionFeatures.length) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Remove Filter';
                  textSpan.classList.add('text-red-500');
                }
              }} onMouseLeave={(e) => {
                const reductionCurrentFeatures = map.current.getSource('comps')._data.features;
                const subjectIFMV = latLongs[0]?.ifmv || 0;
                const reductionFeatures = latLongs.filter(coord => coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false);
                if (reductionCurrentFeatures.length === reductionFeatures.length) {
                  const textSpan = e.currentTarget.querySelector('.text-xs');
                  textSpan.textContent = 'Supports Reduction';
                  textSpan.classList.remove('text-red-500');
                }
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#4ade80', border: '1px solid white', marginRight: '4px', opacity: 0.8 }}></div>
                <span className="text-xs" data-original-text="Supports Reduction">Supports Reduction</span>
              </div>
              {!isCourtView && (
                <div className="flex items-center cursor-pointer hover:bg-gray-100 p-1 rounded transition-opacity legend-item" style={{ opacity: 1, minWidth: '140px' }} onClick={() => {
                  const subjectIFMV = latLongs[0]?.ifmv || 0;
                  const features = latLongs.map(coord => ({
                    type: 'Feature',
                    id: coord.parcel_id,
                    properties: {
                      ...coord,
                      isSubject: coord.isSubject,
                      selected: coord.selected,
                      supportsReduction: coord.adjustedPrice && subjectIFMV
                        ? coord.adjustedPrice < subjectIFMV
                        : false,
                      isModified: false
                    },
                    geometry: {
                      type: 'Point',
                      coordinates: [coord.longitude, coord.latitude]
                    }
                  }));

                  const currentFeatures = map.current.getSource('comps')._data.features;
                  if (currentFeatures.length === features.filter(f => !f.properties.supportsReduction && !f.properties.isSubject).length + 1) { // +1 for subject
                    map.current.getSource('comps').setData({
                      type: 'FeatureCollection',
                      features: features
                    });
                    document.querySelectorAll('.legend-item').forEach(item => {
                      item.style.opacity = '1';
                      const textSpan = item.querySelector('.text-xs');
                      textSpan.textContent = textSpan.getAttribute('data-original-text');
                      textSpan.classList.remove('text-red-500');
                    });
                  } else {
                    // Always include subject point in filtered results
                    const subjectFeature = features.find(f => f.properties.isSubject);
                    const noReductionFeatures = features.filter(f => !f.properties.supportsReduction && !f.properties.isSubject);
                    map.current.getSource('comps').setData({
                      type: 'FeatureCollection',
                      features: [subjectFeature, ...noReductionFeatures]
                    });
                    document.querySelectorAll('.legend-item').forEach(item => {
                      const textSpan = item.querySelector('.text-xs');
                      const originalText = textSpan.getAttribute('data-original-text');
                      item.style.opacity = originalText === 'No Reduction' ? '1' : '0.4';
                      if (originalText === 'No Reduction') {
                        textSpan.textContent = 'Remove Filter';
                        textSpan.classList.add('text-red-500');
                      } else {
                        textSpan.textContent = originalText;
                        textSpan.classList.remove('text-red-500');
                      }
                    });
                  }
                }} onMouseEnter={(e) => {
                  const noReductionCurrentFeatures = map.current.getSource('comps')._data.features;
                  const subjectIFMV = latLongs[0]?.ifmv || 0;
                  const noReductionFeatures = latLongs.filter(coord => !(coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false) && !coord.isSubject);
                  if (noReductionCurrentFeatures.length === noReductionFeatures.length) {
                    const textSpan = e.currentTarget.querySelector('.text-xs');
                    textSpan.textContent = 'Remove Filter';
                    textSpan.classList.add('text-red-500');
                  }
                }} onMouseLeave={(e) => {
                  const noReductionCurrentFeatures = map.current.getSource('comps')._data.features;
                  const subjectIFMV = latLongs[0]?.ifmv || 0;
                  const noReductionFeatures = latLongs.filter(coord => !(coord.adjustedPrice && subjectIFMV ? coord.adjustedPrice < subjectIFMV : false) && !coord.isSubject);
                  if (noReductionCurrentFeatures.length === noReductionFeatures.length) {
                    const textSpan = e.currentTarget.querySelector('.text-xs');
                    textSpan.textContent = 'No Reduction';
                    textSpan.classList.remove('text-red-500');
                  }
                }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ef4444', border: '1px solid white', marginRight: '4px', opacity: 0.8 }}></div>
                  <span className="text-xs" data-original-text="No Reduction">No Reduction</span>
                </div>
              )}
            </>
          )}
        </div>
      <LocationConfirmationOverlay movedPoints={movedPoints} onConfirm={(points) => {
        logger.log('location confirmation overlay onConfirm')
        // Restructure the data for handleProcessDBUpdate
        const records = Object.values(points).map(point => ({
          pid: point.parcel_id,
          Latitude: point.newLatitude,
          Longitude: point.newLongitude
        }));
        // Set loading state first
        setMovedPoints({});
        setIsUpdating(true);
        // Defer async update to next tick to allow overlay to render
        setTimeout(() => {
          updateDB({ confirmed: false, updatedValues: records })
            .then(async (structuredData) => {
              logger.log('Structured data from updateDB:', structuredData);
              // Clear the map loading state after DB update completes
              setIsUpdating(false);
              // Trigger KNN processing (this will show its own loading state)
              await handleProcessDBUpdate(structuredData);
              if (map.current?.getLayer('exclamation-layer')) {
                map.current.removeLayer('exclamation-layer');
              }
            })
            .catch((error) => {
              logger.error('Error structuring data:', error);
              setIsUpdating(false);
            });
        }, 0);
      }}/>
      <LocationConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        movedPoints={movedPoints}
        onConfirm={() => {
          setShowConfirmDialog(false);
          setMovedPoints({});
        }}
      />
      {/* Loading Overlay with debug log */}
      {(isLoading || isUpdating) && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30"
          style={{ zIndex: 9999999 }}
        >
          <div className="bg-white p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-700 font-medium">
              {isUpdating ? 'Updating Map...' : 'Loading Map...'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMap;